import { MatchEvidenceCompare } from './MatchEvidence';
import React from 'react';
import { useLockedOrderPrompt } from './lockedOrder';
import { SavedPartChoiceGuidance, ClarificationQuestion, StructuredAnswer, ClarificationAnswer, PartRefLite, ClarificationHint, ClarificationHintState, ClarificationChatTurn, ClarificationChatReply, ClarificationChatHistoryMessage, NewSearchMode } from './types';
import ClarificationChatPanel, { ClarificationChatCard, ChatMsg, QuestionDiagramThumb } from './ClarificationChatPanel';
import { PartEdit } from '../../../utils/partEdits';
import { resolveCandidate, HintButton, HintBubble, HintCandidateNote } from './ClarificationHintPanel';
import ClarificationDiagramInline from './ClarificationDiagramInline';
import DiagramRefChip from '../../common/DiagramRefChip';
import { refinedRequestLabel } from './narrowingLabel';
import { pollRefinedHint } from './hintRefinement';
import { sentenceCase } from '../../../utils/partLabel';
import { isCatchAllAnswer } from '../../../utils/partTags';
import { ChevronDown, Loader2, Maximize2 } from 'lucide-react';

// ── Structured clarification form ──────────────────────────────────────────────
// Rendered inside the bot bubble when a message envelope carries
// `clarification_questions`. Each question is grouped by `part_name` and rendered
// as radios (single_select), checkboxes (multi_select) or a text input (free_text).
// On submit it builds BOTH a human-readable `message` string (for chat history)
// and the `structured_answer` object the backend expects, then calls onSubmit.
// Styling mirrors RefinePopup (primary #6866ff, bg #f3f2ff).

interface StructuredQuestionFormProps {
  questions: ClarificationQuestion[];
  t: (key: string, opts?: any) => string;
  /** Sends both the human-readable summary and the structured answer payload. */
  onSubmit: (message: string, structuredAnswer: StructuredAnswer) => void;
  /** When true the form is locked (already submitted / loading). */
  disabled?: boolean;
  /** Why the form is locked for THIS viewer (e.g. only the owner may answer);
   *  rendered as a note at the top of the card when `disabled`. */
  lockedNote?: string;
  /**
   * Per-question header label (keyed by question id), resolved to the SAME string
   * shown in the requested-parts list so the question reads consistently.
   * Falls back to `part_label`/`part_name` when absent.
   */
  headerLabels?: Record<string, string>;
  /** Per-question request line — the customer's own words and our reading of
   *  them — shown in the header as “original” → label. */
  requestLines?: Record<string, { original: string; label: string }>;
  /**
   * Previously-submitted answers, keyed by question id, re-attached on history
   * reload. When present the form starts pre-selected from them and is treated
   * as already answered (locked) so the user sees what they chose.
   */
  initialAnswers?: Record<string, {
    choice_guidance?: SavedPartChoiceGuidance[];
    selected_option_ids?: string[];
    free_text?: string | null;
    none_of_the_above?: boolean;
    skipped?: boolean;
  }>;
  /**
   * AI hint per question: asks the backend for a comparison that helps the
   * user tell the candidates apart (vehicle + request + candidates → Gemini).
   * Requested automatically when an answerable question mounts; its per-
   * candidate tags land on the option rows and the "Help me choose" pill opens
   * the full comparison. Absent = nothing (guest/portal pages, or hints
   * disabled). The note is general AI knowledge, not catalog data, and is
   * rendered under a disclaimer.
   */
  onRequestHint?: (question: ClarificationQuestion) => Promise<ClarificationHint>;
  /**
   * Chat about ONE question: the "describe it in your words or ask…" box opens
   * a side panel where the assistant answers from the question's options and
   * the vehicle, and may suggest one option the user can Apply. Absent = no box
   * (guest/portal pages).
   */
  onChatAboutQuestion?: (question: ClarificationQuestion, messages: ClarificationChatTurn[]) => Promise<ClarificationChatReply>;
  /** The stored Part Picker conversation for a question (after a reload). */
  onLoadChatHistory?: (question: ClarificationQuestion) => Promise<ClarificationChatHistoryMessage[]>;
  /** Shown under "Answering" in the chat panel (VIN · order number). */
  chatContext?: string | null;
  /** Open the first question straight into its EPC diagram or the AI chat
   *  sidebar (a skipped question's row actions). A new nonce re-opens. */
  autoOpen?: { mode: 'diagram' | 'chat'; nonce: number } | null;
  /** Hide "Skip for now" (a skipped question answered later from the result). */
  hideSkip?: boolean;
  /** Send a new part request to the main chat (the side chat's "Search for …").
   *  Fallback for pages with no `onPartEdit`. */
  onNewSearch?: (query: string) => void;
  /** Change the requested parts with the explicit edit command (the same one
   *  the tracker's "Edit parts" sends): the side chat's "search X instead"
   *  renames the asked part to X; "also search X" adds X. The backend applies
   *  the edit deterministically and cancels the question about a renamed
   *  part. Preferred over `onNewSearch` and the decline-with-text fallback. */
  onPartEdit?: (edits: PartEdit[]) => void;
  /** Earlier questions of the same narrowing, answered in earlier turns and
   *  shown here — one card walks the customer through every question. */
  previousSteps?: PreviousClarificationStep[];
  /** This answered question is waiting for the next one (the turn is running):
   *  the card shows a loading row instead of looking finished. */
  waitingNext?: boolean;
}

/** One earlier question of the same card and the answer it got. */
export interface PreviousClarificationStep {
  questions: ClarificationQuestion[];
  answers: Record<string, StructuredAnswerLike> | null | undefined;
  /** The request line each question was about, keyed by question id. */
  requestLines?: Record<string, { original: string; label: string }>;
}
type StructuredAnswerLike = {
  selected_option_ids?: string[];
  free_text?: string | null;
  picked_parts?: Array<{ part_name?: string | null; partId?: string | number | null }>;
  skipped?: boolean;
  none_of_the_above?: boolean;
};

/** The line under a choice, customer words only: catalog section codes
 *  ("86-866 - PARACHOQUES TRASERO") and part numbers ("83850AT000") are
 *  dropped — they identify the row for the catalog, not for the customer. */
const SECTION_CODE = /^\s*\d{2,3}\s*[-–]\s*\d{2,4}\b/;
const PART_NUMBER = /^[#\s]*[A-Z0-9][A-Z0-9-]{5,}$/i;
const plainLine = (text?: string | null): string =>
  String(text || '').split(' · ')
    .map(x => plainWords(x))
    .filter(x => x && !SECTION_CODE.test(x) && !(PART_NUMBER.test(x) && /\d/.test(x) && !/\s/.test(x.trim())))
    .join(' · ');

/** A catalog remark that only cites another part number ("Reemplazo logístico
 *  para A 028 250 25 01", "Logistical replacement for A 000 140 07 59") —
 *  logistics for the catalog, noise for the customer. Older cached rows still
 *  carry it inside the name. */
const REPLACEMENT_REMARK = /\s*(?:logistical\s+)?(?:reemplazo(?:\s+log[ií]stico)?\s+(?:de|para)|replacement\s+for|replaces|substitui(?:ção|cao)?(?:\s+log[ií]stica)?(?:\s+(?:de|para))?|ersatz\s+f(?:ü|ue)r)\s+[A-Z]?\s?\d[\d\s]{6,}\d\b/gi;
/** A Daimler-style part number inside text ("A 028 250 25 01"). */
const TEXT_PART_NUMBER = /\b[A-Z]\s?\d{3}\s?\d{3}\s?\d{2}\s?\d{2}(?:\s?\d{2,4})?\b/g;
const plainWords = (text?: string | null): string =>
  String(text || '').replace(REPLACEMENT_REMARK, '').replace(TEXT_PART_NUMBER, '')
    .replace(/\s{2,}/g, ' ').replace(/\s+([,.;:])/g, '$1').trim();

/** The catalog rows of a question read the same in plain words — two or more
 *  rows with one name and nothing customer-readable (area, description) to
 *  tell them apart. The backend flags it (`prefer_diagram`, `indistinct`);
 *  older stored questions are checked here. Narrowing choices ("Front ·
 *  12 parts") and reworded choices are never look-alikes. */
const lookAlikeRows = (q: ClarificationQuestion): boolean => {
  // The narrowing selector found no question a shop can answer
  // (`open_diagram`, the "illustration"): the drawing IS the question.
  if (q.prefer_diagram || q.open_diagram) return true;
  if (q.parts_explained) return false;
  const rows = (q.options || []).filter(o => !o.extra_details?._expert_action);
  if (rows.length < 2 || rows.some(o => (o.candidate_option_ids?.length ?? 0) > 0)) return false;
  if (rows.some(o => o.indistinct)) return true;
  const seen = new Set<string>();
  for (const o of rows) {
    const ed = (o.extra_details && typeof o.extra_details === 'object') ? o.extra_details as Record<string, unknown> : {};
    const key = [plainWords(o.label), plainWords(String(ed['Area'] ?? '')), plainLine(o.description)]
      .join('|').toLowerCase();
    if (seen.has(key)) return true;
    seen.add(key);
  }
  return false;
};

/** The words of an answer, as the customer picked them. */
const answerWords = (q: ClarificationQuestion, a?: StructuredAnswerLike | null): string => {
  if (!a) return '';
  const opt = (id: string) => q.options.find(o => o.id === id) || q.expert_fallback?.options.find(o => o.id === id);
  const words = (a.selected_option_ids || []).map(id => opt(id)?.label || '').filter(Boolean);
  for (const p of a.picked_parts || []) if (p?.part_name) words.push(String(p.part_name));
  if ((a.free_text || '').trim()) words.push(String(a.free_text).trim());
  return words.join(', ');
};

const PRIMARY = '#6866ff';
const PRIMARY_DISABLED = '#c5c4ff';

// Sentinel id for the "type your own" escape-hatch choice added to every
// single/multi-select question. It is NEVER sent to the backend as a real
// option id — when chosen, the typed text is sent as `free_text` instead (which
// makes the backend fall back to a normal search using that text).
const OTHER_OPTION_ID = '__other__';

// Sentinel id for the "I can't find the part in the list" choice. Like
// OTHER_OPTION_ID it is never a real option id — when chosen, the answer is sent
// as `none_of_the_above: true`, which makes the backend report the term as not
// found instead of loading a candidate the user just rejected. The choice opens
// an OPTIONAL context box: whatever is typed there rides along as `free_text`,
// and the backend then re-searches with it instead of dropping the term (see
// is_declined_answer — a decline carrying text is not a decline).
// Exclusive with every other pick on the question, multi_select included: "none
// of these" and "this one" cannot both be true.
const NONE_OPTION_ID = '__none__';
/** Every question is asked the same way — numbered rows, one short line and
 *  the AI hint under each, the EPC diagram one click away ("Open the EPC
 *  diagram") — however many candidates. The one exception: rows that read the
 *  same in plain words open on the diagram (`lookAlikeRows`). */
const SIMPLE_CHOICE_MAX = Number.POSITIVE_INFINITY;


// The app's "settled" green — the same one the cart and In-Cart chips use, so an
// answered question reads as done in the language the rest of the UI already
// speaks. Distinct from PRIMARY, which is the colour of things still awaiting you.
const ANSWERED_GREEN = '#137F67';
const SKIPPED_AMBER = '#9a6700';

const StructuredQuestionForm: React.FC<StructuredQuestionFormProps> = ({
  questions,
  t,
  onSubmit,
  disabled = false,
  lockedNote,
  initialAnswers,
  headerLabels,
  requestLines,
  onRequestHint,
  onChatAboutQuestion,
  onLoadChatHistory,
  chatContext,
  autoOpen,
  // The card offers no Skip button (product decision 2026-09-27); a caller
  // can still turn it on with hideSkip={false}.
  hideSkip = true,
  onNewSearch,
  onPartEdit,
  previousSteps,
  waitingNext = false,
}) => {
  const hasInitialAnswers = !!initialAnswers && Object.keys(initialAnswers).length > 0;

  // AI hint per question id. Fetched AUTOMATICALLY when an answerable list-only
  // question mounts (the backend caches one call per candidate set per day, so
  // reloads and re-renders don't re-bill) and kept for the life of the form so
  // toggling questions in the stepper doesn't re-ask. Diagram questions fetch
  // their own, scoped to the map, inside ClarificationDiagramInline.
  const savedGuidance = React.useRef<Record<string, SavedPartChoiceGuidance[]>>(
    Object.fromEntries(Object.entries(initialAnswers || {}).map(([id, answer]) => [id, answer.choice_guidance || []]))
  );
  const requestAndSaveHint = async (q: ClarificationQuestion): Promise<ClarificationHint> => {
    const hint = await onRequestHint!(q);
    const notes = q.options.flatMap(option => {
      const candidate = resolveCandidate(hint, q, option.id);
      if (!candidate || !option.load?.length) return [];
      return [{ part_name: q.part_name, part_parameters: q.part_parameters || [], part_refs: option.load, candidate, probability_score: option.probability_score }];
    });
    const previous = savedGuidance.current[q.id] || [];
    savedGuidance.current[q.id] = [...previous.filter(note => !notes.some(next => next.candidate.option_id === note.candidate.option_id && JSON.stringify(next.part_refs) === JSON.stringify(note.part_refs))), ...notes];
    return hint;
  };
  const [hints, setHints] = React.useState<Record<string, ClarificationHintState>>({});
  const [hintOpen, setHintOpen] = React.useState<Record<string, boolean>>({});
  // Question ids with a request in flight or done — the auto-run effect and the
  // pill must never double-fire before the state update lands.
  const hintRequested = React.useRef<Set<string>>(new Set());
  // Refinement polls must not set state on an unmounted form.
  const hintPollAlive = React.useRef(true);
  // Set on EVERY mount, not just the first: StrictMode runs mount →
  // cleanup → mount in dev, and an effect that only returns a cleanup
  // leaves the ref false for the rest of the component's life, so every
  // `if (!hintPollAlive.current) return;` below silently drops its result.
  React.useEffect(() => {
    hintPollAlive.current = true;
    return () => { hintPollAlive.current = false; };
  }, []);
  const fetchHint = async (q: ClarificationQuestion) => {
    if (!onRequestHint) return;
    const current = hints[q.id];
    if (current?.status === 'loading' || current?.status === 'ready') return;
    // A failed request may be retried from the pill; the auto-run never loops.
    if (hintRequested.current.has(q.id) && current?.status !== 'error') return;
    hintRequested.current.add(q.id);
    setHints(prev => ({ ...prev, [q.id]: { status: 'loading' } }));
    try {
      const hint = await requestAndSaveHint(q);
      setHints(prev => ({ ...prev, [q.id]: hint?.generated ? { status: 'ready', hint } : { status: 'error' } }));
      // Fast catalog-only answer shown; swap in the grounded one when it lands.
      if (hint?.generated && hint.refining) {
        void pollRefinedHint(
          () => requestAndSaveHint(q),
          refined => setHints(prev => ({ ...prev, [q.id]: { status: 'ready', hint: refined } })),
          () => hintPollAlive.current,
        );
      }
    } catch {
      setHints(prev => ({ ...prev, [q.id]: { status: 'error' } }));
    }
  };
  const openHint = (q: ClarificationQuestion) => {
    setHintOpen(prev => ({ ...prev, [q.id]: true }));
    void fetchHint(q);
  };
  const closeHint = (qid: string) => {
    setHintOpen(prev => ({ ...prev, [qid]: false }));
  };
  // "Show this candidate" from a reference in the comparison: close the bubble,
  // scroll the option row into view and flash its outline for a moment.
  const optionRefs = React.useRef<Map<string, HTMLElement>>(new Map());
  const [flashOption, setFlashOption] = React.useState<string | null>(null);
  const flashTimer = React.useRef<number | null>(null);
  const focusOption = (qid: string, optionId: string) => {
    closeHint(qid);
    const key = `${qid}:${optionId}`;
    requestAnimationFrame(() => optionRefs.current.get(key)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
    setFlashOption(key);
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlashOption(null), 1800);
  };
  React.useEffect(() => () => { if (flashTimer.current) window.clearTimeout(flashTimer.current); }, []);

  // NB: distinct from the older `cantFindPart` string ("Can't find it? Type your
  // own"), which points at the free-text hatch — this one declares the part absent.
  const noneLabel = t('chat.clarification.partNotInList', {
    defaultValue: "I can't find the part in the list",
  });
  const skipLabel = t('chat.clarification.skipped', { defaultValue: 'Skipped — answer it later from the result' });

  // An answered card is one line with the answer; this reopens the full
  // question (Q / A, the resolved parts, "View on diagram").
  const [answeredOpen, setAnsweredOpen] = React.useState(false);
  // Which earlier question of the card is open (index into the steps); null =
  // the current one.
  const [viewStep, setViewStep] = React.useState<number | null>(null);
  // Question ids whose EPC diagram is loading after "Open the EPC diagram".
  const [mapOpening, setMapOpening] = React.useState<Record<string, boolean>>({});
  // The part-request "Details": the answers so far and how each cut the list.
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  // "Skip for now": the question is postponed — the other lines go ahead and
  // this one stays open in the result, where it can be answered later.
  const [skippedQ, setSkippedQ] = React.useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    for (const q of questions) if (initialAnswers?.[q.id]?.skipped) init[q.id] = true;
    return init;
  });

  // A question renders its section diagram inline (as the primary selection
  // surface) when at least one option carries a catalog `load` ref
  // (group_id + section_id) we can fetch a diagram for.
  const questionHasDiagram = (q: ClarificationQuestion): boolean =>
    (q.options || []).some(o => (o.load || []).some(r => !!r.group_id && !!r.section_id));
  // A few candidates are a simple choice: numbered rows, one click each, the
  // same shape as "Front / Rear". The EPC diagram stays one click away
  // ("Open the EPC diagram") instead of being the whole question. Only a long
  // list opens on the map, where the drawing is the faster way to tell parts
  // apart.
  // Customer feedback 2026-10-02: "if the system cannot help, just show the
  // diagram". Rows that read the same in plain words (two "Embrague monodisco
  // seco") are not a choice the customer can make from a list — the question
  // opens on the drawing, where position and shape tell them apart.
  const usesMap = (q: ClarificationQuestion): boolean =>
    q.question_type !== 'free_text' && questionHasDiagram(q)
    && (q.options.filter(o => !o.extra_details?._expert_action).length > SIMPLE_CHOICE_MAX
      || lookAlikeRows(q));
  // The question a map opened on demand draws: the question itself, or — for
  // a narrowing question ("Front or rear?") — the candidates behind it, under
  // the narrowing question's id so a part clicked there answers it directly
  // (no "Show diagram" round trip).
  const mapQuestionFor = (q: ClarificationQuestion): ClarificationQuestion | null => {
    if (q.question_type === 'free_text') return null;
    if (questionHasDiagram(q)) return q;
    const fb = q.expert_fallback;
    return fb && questionHasDiagram(fb) ? { ...fb, id: q.id, text: fb.text || q.text } : null;
  };
  const opensMapOnDemand = (q: ClarificationQuestion): boolean =>
    !!mapQuestionFor(q) && !usesMap(q);
  // An option by id — on the question, or on the candidates behind it (a part
  // clicked on a narrowing question's diagram).
  const findOption = (q: ClarificationQuestion, id: string) =>
    q.options.find(o => o.id === id) || q.expert_fallback?.options.find(o => o.id === id);

  // Per-question state. Selections keyed by question id → set of option ids;
  // free-text answers keyed by question id → typed string. Seeded from
  // initialAnswers (history reload) so a previously-answered form shows the pick.
  const [selections, setSelections] = React.useState<Record<string, string[]>>(() => {
    const init: Record<string, string[]> = {};
    if (initialAnswers) {
      for (const q of questions) {
        const a = initialAnswers[q.id];
        if (!a) continue;
        const ids = [...(a.selected_option_ids ?? [])];
        // A stored decline reads back as the "can't find it" choice — with its
        // context text (if any) in that choice's box, NOT as "type your own".
        if (q.question_type !== 'free_text' && a.none_of_the_above) {
          ids.push(NONE_OPTION_ID);
        } else if (q.question_type !== 'free_text' && (a.free_text ?? '').trim()) {
          // A typed answer on a select question maps to the "type your own" choice.
          ids.push(OTHER_OPTION_ID);
        }
        if (ids.length) init[q.id] = ids;
      }
    }
    return init;
  });
  const [texts, setTexts] = React.useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    if (initialAnswers) {
      for (const q of questions) {
        const a = initialAnswers[q.id];
        if (a && (a.free_text ?? '').trim()) init[q.id] = a.free_text as string;
      }
    }
    return init;
  });
  // Concrete catalog parts the user clicked directly on a section diagram that
  // aren't one of the listed options — keyed by question id. Loaded as-is on the
  // answer turn (fast-path), distinct from the free-text "type your own" path.
  const [pickedParts, setPickedParts] = React.useState<Record<string, PartRefLite[]>>(() => {
    const init: Record<string, PartRefLite[]> = {};
    if (initialAnswers) {
      for (const q of questions) {
        const refs = (initialAnswers[q.id] as any)?.picked_parts as PartRefLite[] | undefined;
        if (refs && refs.length) init[q.id] = refs;
      }
    }
    return init;
  });
  const pickPart = (questionId: string, ref: PartRefLite, clearOptions: boolean) => {
    // Concrete part picks are ALWAYS additive — the user can select MULTIPLE parts
    // (e.g. both the left and right fender). De-dupe by partId.
    setPickedParts(prev => {
      const current = prev[questionId] ?? [];
      if (current.some(r => String(r.partId) === String(ref.partId))) return prev;
      return { ...prev, [questionId]: [...current, ref] };
    });
    // single_select: concrete parts supersede a selected option / typed text for
    // this question (but multiple parts are still allowed).
    if (clearOptions) {
      setSelections(prev => ({ ...prev, [questionId]: [] }));
      setTexts(prev => ({ ...prev, [questionId]: '' }));
    } else {
      // multi_select: picking a real part contradicts "can't find it in the list".
      setSelections(prev => (
        (prev[questionId] ?? []).includes(NONE_OPTION_ID)
          ? { ...prev, [questionId]: (prev[questionId] ?? []).filter(id => id !== NONE_OPTION_ID) }
          : prev
      ));
    }
  };
  const unpickPart = (questionId: string, partId: string) => {
    setPickedParts(prev => ({
      ...prev,
      [questionId]: (prev[questionId] ?? []).filter(r => String(r.partId) !== String(partId)),
    }));
  };
  // Pre-answered (reloaded) forms start locked so they read as already submitted.
  const [submitted, setSubmitted] = React.useState(hasInitialAnswers);
  // When more than one question is asked we walk them one at a time (a stepper)
  // instead of cramming them all into a single form. `step` is the index of the
  // currently-visible question. Answers are still collected across steps and sent
  // together on the final submit (the backend fast-path resume expects the whole
  // StructuredAnswer payload at once — see automotive_agent resume_router_node).
  const [step, setStep] = React.useState(0);

  // ── Selecting is not confirming ───────────────────────────────────────────
  // Picking an option — in the list or on the diagram — ONLY records the pick.
  // Nothing advances, nothing sends. Choosing a part is exploratory: users point
  // at callouts to see what they are, compare two candidates, change their mind.
  // Acting on a click mid-decision confirms a choice that was still being made,
  // and on a clarification that costs a search turn to undo.
  //
  // The only things that ACT are explicit: the form's Next / Submit button, and
  // the Confirm button inside the opened diagram.

  const toggleSingle = (questionId: string, optionId: string) => {
    setSelections(prev => ({ ...prev, [questionId]: [optionId] }));
    // single_select is mutually exclusive — choosing an option drops any concrete
    // diagram part pick for this question.
    setPickedParts(prev => (prev[questionId]?.length ? { ...prev, [questionId]: [] } : prev));
  };

  const toggleMulti = (questionId: string, optionId: string) => {
    setSelections(prev => {
      const current = prev[questionId] ?? [];
      const next = current.includes(optionId)
        ? current.filter(id => id !== optionId)
        : [...current.filter(id => id !== NONE_OPTION_ID), optionId];
      return { ...prev, [questionId]: next };
    });
  };

  // "I can't find the part in the list" — exclusive with everything else on this
  // question (options, a typed answer, diagram picks), on multi_select too.
  // Clicking it again clears it, so the choice is never a trap.
  const toggleNone = (questionId: string) => {
    // Declining IS an answer (see isQuestionAnswered), so it advances like any
    // other definitive pick; clicking it again un-answers and cancels.
    const willClear = (selections[questionId] ?? []).includes(NONE_OPTION_ID);
    setSelections(prev => {
      const isSet = (prev[questionId] ?? []).includes(NONE_OPTION_ID);
      return { ...prev, [questionId]: isSet ? [] : [NONE_OPTION_ID] };
    });
    setTexts(prev => (prev[questionId] ? { ...prev, [questionId]: '' } : prev));
    setPickedParts(prev => (prev[questionId]?.length ? { ...prev, [questionId]: [] } : prev));
    clearOtherKey(questionId);
  };

  const setText = (questionId: string, value: string) => {
    setTexts(prev => ({ ...prev, [questionId]: value }));
  };

  // Unique row key of the diagram part chosen for the "type your own" pick, keyed
  // by question id. Pins the diagram's green highlight to the EXACT clicked part
  // (several rows can share a catalog name). Cleared when the pick is cleared or
  // replaced by typed text (a typed answer maps to no specific listed part).
  const [otherKeys, setOtherKeys] = React.useState<Record<string, string>>({});
  const clearOtherKey = (questionId: string) =>
    setOtherKeys(prev => {
      if (!(questionId in prev)) return prev;
      const next = { ...prev };
      delete next[questionId];
      return next;
    });

  // Pick a part the listed options didn't cover (the "type your own" path):
  // select the escape-hatch choice and stash the typed/clicked text. Used both
  // by the free-text box (no part key) and by clicking a non-related part on the
  // diagram (carries the clicked part's unique key).
  const selectOther = (q: ClarificationQuestion, text: string, partKey?: string) => {
    if (q.question_type === 'single_select') {
      setSelections(prev => ({ ...prev, [q.id]: [OTHER_OPTION_ID] }));
    } else {
      setSelections(prev => {
        const current = (prev[q.id] ?? []).filter(id => id !== NONE_OPTION_ID);
        return current.includes(OTHER_OPTION_ID) ? { ...prev, [q.id]: current } : { ...prev, [q.id]: [...current, OTHER_OPTION_ID] };
      });
    }
    setText(q.id, text);
    if (partKey) setOtherKeys(prev => ({ ...prev, [q.id]: partKey }));
    else clearOtherKey(q.id);
    // The answer here is the text, which the user is still writing.
  };

  // Clear the "type your own" pick (clicking a chosen non-related part again).
  const clearOther = (q: ClarificationQuestion) => {
    setText(q.id, '');
    setSelections(prev => ({ ...prev, [q.id]: (prev[q.id] ?? []).filter(id => id !== OTHER_OPTION_ID) }));
    clearOtherKey(q.id);
  };

  // The DIAGRAM's selection path is toggleMulti for every question type: a map
  // click only ever SELECTS (clicking again un-picks), several parts may be
  // picked, and the open diagram's Confirm button is the one thing that acts.

  // "Clear selection" in the diagram: every pick on the question goes.
  const clearSelection = (questionId: string) => {
    setSelections(prev => ({ ...prev, [questionId]: [] }));
    setPickedParts(prev => (prev[questionId]?.length ? { ...prev, [questionId]: [] } : prev));
    setTexts(prev => (prev[questionId] ? { ...prev, [questionId]: '' } : prev));
    setOtherKeys(prev => { if (!(questionId in prev)) return prev; const next = { ...prev }; delete next[questionId]; return next; });
  };

  // Parts already chosen in the OTHER questions of this form, as diagram refs.
  // When two questions share the same section diagram (e.g. front + rear door
  // moulding on one exploded view), the later question's diagram marks these as
  // "already selected" so the user keeps context of earlier picks and doesn't
  // choose the same part twice.
  const priorPicksFor = (questionId: string): PartRefLite[] => {
    const refs: PartRefLite[] = [];
    for (const oq of questions) {
      if (oq.id === questionId || oq.question_type === 'free_text') continue;
      for (const optId of (selections[oq.id] ?? [])) {
        if (optId === OTHER_OPTION_ID || optId === NONE_OPTION_ID) continue;
        const opt = oq.options.find(o => o.id === optId);
        for (const ref of (opt?.load || [])) {
          if (!ref.group_id || !ref.section_id) continue;
          refs.push({ ...ref, part_name: ref.part_name ?? opt?.label });
        }
      }
      refs.push(...(pickedParts[oq.id] ?? []));
    }
    return refs;
  };

  // A question is answered when: free_text → non-empty trimmed; select → a real
  // option is picked (single = exactly one, multi = >=1) OR the "type your own"
  // choice is picked WITH non-empty text. If "type your own" is picked it MUST
  // have text (an empty escape-hatch is not an answer).
  const isQuestionAnswered = (
    q: ClarificationQuestion,
    sels: Record<string, string[]> = selections,
    picks: Record<string, PartRefLite[]> = pickedParts,
  ): boolean => {
    if (skippedQ[q.id]) return true;
    if (q.question_type === 'free_text') {
      return (texts[q.id] ?? '').trim().length > 0;
    }
    const sel = sels[q.id] ?? [];
    // "Can't find it in the list" is a complete answer on its own.
    if (sel.includes(NONE_OPTION_ID)) return true;
    const hasOther = sel.includes(OTHER_OPTION_ID);
    const realCount = sel.filter(id => id !== OTHER_OPTION_ID).length;
    const typedNonEmpty = (texts[q.id] ?? '').trim().length > 0;
    const pickedCount = (picks[q.id] ?? []).length;
    if (hasOther && !typedNonEmpty) return false;  // chose "type your own" but typed nothing
    // Any number of picks answers the question, single_select included: the
    // diagram picks with checkboxes because one request can need several parts
    // (both fenders, a cover and its clips). The card's radios still replace.
    return realCount >= 1 || pickedCount >= 1 || (hasOther && typedNonEmpty);
  };

  const allAnswered = questions.every(q => isQuestionAnswered(q));

  // Questions the side chat REPLACED with another part ("not the door — the
  // door handle"): sent as a decline carrying the new term, so the backend
  // drops the asked part and re-searches with it in its place.
  const replacedBy = React.useRef<Record<string, string>>({});
  // `sels` / `picks` / `txts` let the chat panel answer in one go (state
  // updates land after this call returns).
  const submitAnswers = (
    sels: Record<string, string[]>,
    picks: Record<string, PartRefLite[]>,
    skips: Record<string, boolean> = skippedQ,
    txts: Record<string, string> = texts,
  ) => {
    if (!questions.every(q => skips[q.id] || isQuestionAnswered(q, sels, picks)) || disabled || submitted) return;

    const answers: ClarificationAnswer[] = [];
    const messageParts: string[] = [];

    for (const q of questions) {
      if (skips[q.id]) {
        // Postponed: nothing picked, nothing declined — the line stays open.
        answers.push({ question_id: q.id, selected_option_ids: [], free_text: null, skipped: true });
        messageParts.push(`${headerLabels?.[q.id] || q.part_label || q.part_name}: ${t('chat.clarification.skipForNow', { defaultValue: 'Skip for now' })}`);
        continue;
      }
      if (q.question_type === 'free_text') {
        const value = (txts[q.id] ?? '').trim();
        answers.push({ question_id: q.id, selected_option_ids: [], free_text: value });
        // Display name for the user-visible answer bubble: the localized header
        // label, never the English search-language part_name.
        messageParts.push(`${headerLabels?.[q.id] || q.part_label || q.part_name}: ${value}`);
      } else if ((sels[q.id] ?? []).includes(NONE_OPTION_ID)) {
        // Decline: no option, no parts — just the flag, plus whatever context the
        // user typed. Without text the backend reports the term as not found;
        // with it, the decline becomes a re-search using that text.
        const context = (txts[q.id] ?? '').trim();
        const replaced = !!context && replacedBy.current[q.id] === context;
        answers.push({
          question_id: q.id,
          selected_option_ids: [],
          free_text: context || null,
          none_of_the_above: true,
          ...(replaced ? { replaces_part: true } : {}),
        });
        const label = headerLabels?.[q.id] || q.part_label || q.part_name;
        messageParts.push(
          replaced
            // "Door → front door handle": the asked part is swapped, not refined.
            ? `${label} → ${context}`
            : `${label}: ${noneLabel}` + (context ? ` — ${context}` : '')
        );
      } else {
        const sel = sels[q.id] ?? [];
        const hasOther = sel.includes(OTHER_OPTION_ID);
        const realIds = sel.filter(id => id !== OTHER_OPTION_ID && id !== NONE_OPTION_ID);
        const typed = (txts[q.id] ?? '').trim();
        // The "type your own" pick is sent as free_text, never as an option id —
        // its presence makes the backend re-run the search with the typed text.
        const freeText = hasOther && typed ? typed : null;
        // Concrete parts clicked on the diagram — loaded as-is on the answer turn.
        const picked = picks[q.id] ?? [];
        answers.push({
          question_id: q.id,
          selected_option_ids: realIds,
          free_text: freeText,
          ...(picked.length ? { picked_parts: picked } : {}),
        });
        const labels = realIds.map(id => findOption(q, id)?.label ?? id);
        // What the customer saw for each id, so the backend can catch a copy
        // of the question that disagrees with its own on option ids.
        answers[answers.length - 1].selected_option_labels = [...labels];
        for (const r of picked) labels.push(r.part_name || r.partId);
        if (freeText) labels.push(freeText);
        messageParts.push(`${headerLabels?.[q.id] || q.part_label || q.part_name}: ${labels.join(', ')}`);
      }
    }

    const message = messageParts.join('; ');
    setSubmitted(true);
    answers.forEach(answer => { answer.choice_guidance = savedGuidance.current[answer.question_id] || []; });
    onSubmit(message, { answers });
  };
  const handleSubmit = () => submitAnswers(selections, pickedParts);
  // Skip the question on screen: on a multi-question card move to the next
  // one; on the last (or only) one, send the form.
  const skipQuestion = (q: ClarificationQuestion) => {
    const next = { ...skippedQ, [q.id]: true };
    setSkippedQ(next);
    if (questions.length > 1 && step < questions.length - 1) setStep(s => s + 1);
    else submitAnswers(selections, pickedParts, next);
  };

  const isLocked = disabled || submitted;
  const promptTakeOrder = useLockedOrderPrompt();

  // "Answering inside the fullscreen map": set when the user opens a question's
  // map, kept while they confirm questions from within it, so each next
  // question's map opens fullscreen on mount and the whole clarification is
  // answered in one place. Dropped when they cancel/close the map, when the
  // form submits, or when the flow reaches a question without a diagram.
  const [fullscreenFlow, setFullscreenFlow] = React.useState(false);
  // Chip clicked on the card → select it and open its diagram, keyed by
  // question id so only that question's map reacts.
  const [openRequests, setOpenRequests] = React.useState<Record<string, { optionId: string; nonce: number }>>({});
  React.useEffect(() => {
    if (fullscreenFlow && (isLocked || !usesMap(questions[step]))) setFullscreenFlow(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, isLocked]);

  // A form is "answered" once submitted this session or reloaded with stored
  // answers. Distinct from `isLocked` (which is also true when the form is just
  // disabled mid-load) — drives the read-only "answered" styling.
  const isAnswered = submitted || hasInitialAnswers;
  // A hint is read-only content, so it does NOT follow `isLocked`: a viewer who
  // cannot answer (not the order owner, or a turn in flight) must still see the
  // comparison and the per-candidate tags. Only an ANSWERED question skips the
  // call — its guidance was saved with the answer and renders from there.
  // Not on a narrowing question ("Left / Right / Both", "Front / Rear"): its
  // choices are the side/position itself, so a hint only echoes the label.
  const isNarrowingQuestion = (q: ClarificationQuestion): boolean =>
    (q.options || []).some(o => o.extra_details?._expert_action === 'show_diagram');
  // Only on a map question: a few plain rows ("Left / Right") need no AI
  // comparison — "Help me choose" belongs to the diagram selection.
  const hintAvailable = (q: ClarificationQuestion): boolean =>
    !!onRequestHint && !isAnswered && q.question_type !== 'free_text' && (q.options?.length ?? 0) > 0
    && !isNarrowingQuestion(q) && usesMap(q);
  // The AI comparison's per-row tags (side, outer / inner, what the part is)
  // show under every row of a question whose rows are catalog parts, so the
  // customer can tell them apart — no "Help me choose" pill. A narrowing
  // question's rows are the answer itself ("Front / Rear"): no tags there.
  // A confirmation of the one part we found ("is this the part you need?")
  // needs its hints most of all: it is the row the customer must judge alone.
  const rowHints = (q: ClarificationQuestion): boolean =>
    !!onRequestHint && !isAnswered && q.question_type !== 'free_text'
    && (q.options?.length ?? 0) > ((q as any).question_kind === 'confirm_candidate' ? 0 : 1)
    && !isNarrowingQuestion(q) && !usesMap(q);

  // Auto-run the hint for every answerable list-only question as soon as it is
  // on screen — the user should not have to ask to learn what separates the
  // candidates. Diagram questions run theirs once the map has loaded.
  React.useEffect(() => {
    for (const q of questions) {
      if (!(hintAvailable(q) || rowHints(q)) || usesMap(q)) continue;
      void fetchHint(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, isAnswered, !!onRequestHint]);

  // The AI guidance the RESULTS show (side, finish, … on each loaded part) is
  // the comparison saved with the answer. Plain rows and "Front / Rear" show
  // no comparison, so nothing was saved unless the customer happened to open
  // the diagram — and the results had no AI tags. Run it quietly for the
  // parts behind every open question (a narrowing question's candidates
  // included) so it is saved with whatever answer is given. No UI here.
  const guidanceRequested = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    if (!onRequestHint || isAnswered || isLocked) return;
    for (const q of questions) {
      if (usesMap(q) || rowHints(q) || guidanceRequested.current.has(q.id)) continue;   // the map / row hints run their own
      const target = mapQuestionFor(q) ?? (q.options.some(o => (o.load || []).length) ? q : null);
      if (!target || target.options.filter(o => (o.load || []).length).length < 2) continue;
      guidanceRequested.current.add(q.id);
      void requestAndSaveHint(target)
        .then(hint => {
          if (hint?.generated && hint.refining) {
            void pollRefinedHint(() => requestAndSaveHint(target), () => {}, () => hintPollAlive.current);
          }
        })
        .catch(() => { /* display-only: the backend backfills from its cache */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, isAnswered, isLocked, !!onRequestHint]);

  // Stepper applies whenever there is more than one question still open —
  // including a form locked because the viewer is not the order owner. Stacking
  // every question with its full diagram made the chat unreadable. Answered
  // forms render every question at once (in the answered card) so the
  // user can review all of their picks together.
  const isStepper = !isAnswered && questions.length > 1;
  const lastStep = questions.length - 1;
  const currentQuestion = questions[step];
  const visibleQuestions = isStepper ? [currentQuestion] : questions;
  // Diagram questions are completed from the fullscreen map footer. Keeping a
  // second Back/Next/Submit row on the outer card creates two competing places
  // to finish the same answer.
  // …except once a question is declined: "not in the list" is answered on the
  // form (with its context box), not on the map, so that row is the only place
  // left to submit from. Without this the decline was a dead end — the map has
  // nowhere to type and the form had no button.
  const visibleQuestionsUseDiagram = visibleQuestions.length > 0 && visibleQuestions.every(
    q => usesMap(q),
  ) && !visibleQuestions.some(q => (selections[q.id] ?? []).includes(NONE_OPTION_ID));

  // A locked (non-owner) viewer cannot answer, so paging is not gated on it.
  const goNext = () => {
    if (isStepper && step < lastStep && (isLocked || isQuestionAnswered(currentQuestion))) {
      setStep(s => Math.min(s + 1, lastStep));
    }
  };
  const goBack = () => {
    if (isStepper && step > 0) setStep(s => Math.max(s - 1, 0));
  };

  // "I can't find the part in the list" + its optional context box. Rendered on
  // EVERY answerable question, diagram or list: a diagram question used to offer
  // the decline only inside the fullscreen map, where it was a bare toggle — the
  // user clicked it, saw a highlight and had nowhere to say what the part
  // actually is. Declining must always come with a way to refine the request.
  // ── Chat about this question ─────────────────────────────────────────────
  // The "AI Assistant chat" card on the question opens a sidebar scoped to it.
  // One thread per question, kept here so the card can show "Chat open · N"
  // and reopening continues the conversation. A suggestion is applied only by
  // the user (Apply), which answers the question like picking that option.
  const [threads, setThreads] = React.useState<Record<string, ChatMsg[]>>({});
  const [chatFor, setChatFor] = React.useState<string | null>(null);
  const [chatBusy, setChatBusy] = React.useState<Record<string, boolean>>({});
  const [chatError, setChatError] = React.useState<Record<string, boolean>>({});
  // A search the customer confirmed in the side chat, run on the next render.
  const [autoSearch, setAutoSearch] = React.useState<{ questionId: string; query: string; mode: NewSearchMode } | null>(null);
  const chatAlive = React.useRef(true);
  // Set on EVERY mount, not just the first: StrictMode runs mount →
  // cleanup → mount in dev, and an effect that only returns a cleanup
  // leaves the ref false for the rest of the component's life, so every
  // `if (!chatAlive.current) return;` below silently drops its result.
  React.useEffect(() => {
    chatAlive.current = true;
    return () => { chatAlive.current = false; };
  }, []);
  const chatAvailable = (q: ClarificationQuestion): boolean =>
    !!onChatAboutQuestion && !isAnswered && q.question_type !== 'free_text' && (q.options?.length ?? 0) > 0;
  // After a reload the conversation lives server-side (the Part Picker keeps
  // it); pull it once per open question so the row shows "Chat open · N" and
  // the sidebar continues where it stopped. A thread started meanwhile wins.
  const historyRequested = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    if (!onLoadChatHistory) return;
    for (const q of questions) {
      if (!chatAvailable(q) || historyRequested.current.has(q.id)) continue;
      historyRequested.current.add(q.id);
      onLoadChatHistory(q).then(msgs => {
        if (!chatAlive.current || !msgs?.length) return;
        setThreads(prev => (prev[q.id]?.length ? prev : {
          ...prev,
          [q.id]: msgs.map(m => ({
            role: m.role, content: m.content, image: m.image ?? null,
            suggestedOptionId: m.suggested_option_id ?? null, suggestedLabel: m.suggested_label ?? null,
            newSearch: m.new_search ?? null, newSearchMode: m.new_search_mode ?? null,
          })),
        }));
      }).catch(() => { /* no history — start fresh */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, isAnswered, !!onLoadChatHistory]);
  const sendChat = async (q: ClarificationQuestion, text: string, image?: string | null) => {
    if (!onChatAboutQuestion || (!text.trim() && !image) || chatBusy[q.id]) return;
    const next: ChatMsg[] = [...(threads[q.id] ?? []), { role: 'user', content: text.trim(), image: image ?? null }];
    setThreads(prev => ({ ...prev, [q.id]: next }));
    setChatFor(q.id);
    setChatBusy(prev => ({ ...prev, [q.id]: true }));
    setChatError(prev => ({ ...prev, [q.id]: false }));
    try {
      const reply = await onChatAboutQuestion(q, next.map(m => ({ role: m.role, content: m.content, image: m.image ?? null })));
      if (!chatAlive.current) return;
      setThreads(prev => ({ ...prev, [q.id]: [...(prev[q.id] ?? []), {
        role: 'assistant', content: reply.reply,
        suggestedOptionId: reply.suggested_option_id ?? null, suggestedLabel: reply.suggested_label ?? null,
        newSearch: reply.new_search ?? null, newSearchMode: reply.new_search_mode ?? null,
      }] }));
      // The customer already said yes in words ("sí, búscala"): run it now,
      // from an effect so it reads this render's selections, not the ones
      // captured when the message was sent.
      if (reply.new_search && reply.new_search_confirmed) {
        setAutoSearch({ questionId: q.id, query: reply.new_search, mode: reply.new_search_mode === 'replace' ? 'replace' : 'add' });
      }
    } catch {
      if (chatAlive.current) setChatError(prev => ({ ...prev, [q.id]: true }));
    } finally {
      if (chatAlive.current) setChatBusy(prev => ({ ...prev, [q.id]: false }));
    }
  };
  const applyChoice = (q: ClarificationQuestion, optionId: string) => {
    if (isLocked || !q.options.some(o => o.id === optionId)) return;
    const sels = { ...selections, [q.id]: [optionId] };
    const picks = { ...pickedParts, [q.id]: [] as PartRefLite[] };
    setSelections(sels);
    setPickedParts(picks);
    setChatFor(null);
    if (questions.every(x => isQuestionAnswered(x, sels, picks))) {
      submitAnswers(sels, picks);
    } else if (isStepper && step < lastStep) {
      setStep(s => Math.min(s + 1, lastStep));
    }
  };
  // The side chat found the customer meant a DIFFERENT part than the one asked
  // about ("not the door — the door handle"). Answer the question with that
  // part in place of the asked one: a decline carrying the new term, which the
  // backend reads as "drop the asked part, search this instead" — the same
  // line is re-searched, nothing is added beside it. The sidebar closes and
  // the order resumes like after any answer.
  const replaceSearch = (q: ClarificationQuestion, query: string) => {
    const term = query.trim();
    if (isLocked || !term) return;
    replacedBy.current[q.id] = term;
    const sels = { ...selections, [q.id]: [NONE_OPTION_ID] };
    const picks = { ...pickedParts, [q.id]: [] as PartRefLite[] };
    const txts = { ...texts, [q.id]: term };
    setSelections(sels);
    setPickedParts(picks);
    setTexts(txts);
    setChatFor(null);
    if (questions.every(x => isQuestionAnswered(x, sels, picks))) {
      submitAnswers(sels, picks, skippedQ, txts);
    } else if (isStepper && step < lastStep) {
      setStep(s => Math.min(s + 1, lastStep));
    }
  };
  // Where an offered search lands. With the edit command available (signed-in
  // chat): "replace" renames the asked part — the customer's own wording for
  // the line, as the tracker shows it — to the new part, and "add" adds one;
  // the backend re-searches with the edit applied and cancels the question.
  // Without it: "replace" answers the question with the new term (decline
  // carrying it) and "add" goes to the main chat as a message.
  const runSearch = (q: ClarificationQuestion, query: string, mode: NewSearchMode) => {
    if (isLocked) return;
    const term = query.trim();
    if (!term) return;
    if (onPartEdit) {
      const asked = (requestLines?.[q.id]?.original || q.part_label || q.part_name || '').trim();
      setChatFor(null);
      onPartEdit(mode === 'replace' && asked ? [{ original: asked, value: term }] : [{ original: '', value: term }]);
      return;
    }
    if (mode === 'replace') { replaceSearch(q, term); return; }
    if (!onNewSearch) return;
    setChatFor(null);
    onNewSearch(term);
  };
  React.useEffect(() => {
    if (!autoSearch) return;
    setAutoSearch(null);
    const q = questions.find(x => x.id === autoSearch.questionId);
    if (q) runSearch(q, autoSearch.query, autoSearch.mode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSearch]);
  // A row action asked to open this question in its diagram or the AI chat.
  React.useEffect(() => {
    const q = questions[0];
    if (!autoOpen || !q || isLocked) return;
    if (autoOpen.mode === 'chat') {
      if (onChatAboutQuestion) setChatFor(q.id);
      return;
    }
    if (questionHasDiagram(q)) {
      if (usesMap(q)) setFullscreenFlow(true);
      setOpenRequests(prev => ({ ...prev, [q.id]: { optionId: '', nonce: autoOpen.nonce } }));
    } else if (mapQuestionFor(q)) {
      // A narrowing question: the candidates' diagram, in place.
      setOpenRequests(prev => ({ ...prev, [q.id]: { optionId: '', nonce: autoOpen.nonce } }));
    } else {
      const show = q.options.find(o => o.extra_details?._expert_action === 'show_diagram');
      if (show) applyChoice(q, show.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen?.nonce]);

  // The illustration: no question could tell the remaining parts apart, so
  // the drawing opens on its own with them highlighted (`open_diagram`, set by
  // the narrowing selector). Once per question; never on a locked card.
  const openedDiagrams = React.useRef<Set<string>>(new Set());
  const diagramQuestion = isStepper ? currentQuestion : questions[0];
  React.useEffect(() => {
    const q = diagramQuestion;
    if (!q || isLocked || !q.open_diagram || openedDiagrams.current.has(q.id)) return;
    if (!questionHasDiagram(q)) return;
    openedDiagrams.current.add(q.id);
    if (usesMap(q)) setFullscreenFlow(true);
    setOpenRequests(prev => ({ ...prev, [q.id]: { optionId: '', nonce: Date.now() } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagramQuestion?.id, isLocked]);

  // The chat sidebar's "Diagram" / "Show this on the diagram": close the chat
  // and open the question's EPC map, on the suggested part when there is one.
  // A narrowing question has no map of its own — "Show diagram" leads there.
  const openDiagramFor = (q: ClarificationQuestion, optionId?: string) => {
    setChatFor(null);
    if (questionHasDiagram(q)) {
      if (usesMap(q)) setFullscreenFlow(true);
      setOpenRequests(prev => ({ ...prev, [q.id]: { optionId: optionId || '', nonce: Date.now() } }));
      return;
    }
    if (mapQuestionFor(q)) {
      setOpenRequests(prev => ({ ...prev, [q.id]: { optionId: optionId || '', nonce: Date.now() } }));
      return;
    }
    const show = diagramAction(q);
    if (show) applyChoice(q, show.id);
  };

  // The numbered answer rows; "Show diagram" is a navigation action, rendered
  // as the footer button instead.
  // The answers a question offers. A catch-all ("Multiple parts" = every
  // candidate) rules nothing out, so it is no answer — except the "Both" the
  // backend generates on a pair (`multi` beside exactly two answers). Older
  // saved questions carry a `multi` "Multiple parts" over 3 answers: hidden.
  const rowOptions = (q: ClarificationQuestion) => {
    const rows = q.options.filter(o => !o.extra_details?._expert_action);
    const answers = rows.map(o => ({ o, optionIds: o.candidate_option_ids ?? [] }));
    const isBoth = (o: { multi?: boolean }) => !!o.multi && rows.length === 3;
    return answers.filter(a => isBoth(a.o) || !isCatchAllAnswer(a, answers)).map(a => a.o);
  };
  const diagramAction = (q: ClarificationQuestion) =>
    q.options.find(o => o.extra_details?._expert_action === 'show_diagram');
  // Catalog candidates behind the question — "Show diagram" keeps every one.
  const matchCount = (q: ClarificationQuestion): number =>
    diagramAction(q)?.candidate_option_ids?.length ?? rowOptions(q).length;
  // "Press 1–N to choose" on the open single-select list question.
  const keyQuestion = !isLocked && currentQuestion && currentQuestion.question_type === 'single_select'
    && !usesMap(currentQuestion) ? currentQuestion : null;
  React.useEffect(() => {
    if (!keyQuestion) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || !/^[1-9]$/.test(e.key)) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      const opt = rowOptions(keyQuestion)[Number(e.key) - 1];
      if (!opt) return;
      e.preventDefault();
      applyChoice(keyQuestion, opt.id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyQuestion, selections, pickedParts]);
  const renderAsk = (q: ClarificationQuestion) => {
    if (!chatAvailable(q)) return null;
    return (
      <>
        <ClarificationChatCard
          question={q}
          messageCount={(threads[q.id] ?? []).length}
          onSend={(text, image) => { void sendChat(q, text, image); }}
          onOpen={() => setChatFor(q.id)}
          t={t}
        />
        {chatFor === q.id && (
          <ClarificationChatPanel
            question={q}
            context={chatContext}
            messages={threads[q.id] ?? []}
            loading={!!chatBusy[q.id]}
            error={!!chatError[q.id]}
            onSend={(text, image) => { void sendChat(q, text, image); }}
            onApply={(optionId) => applyChoice(q, optionId)}
            applyDisabled={isLocked}
            onClose={() => setChatFor(null)}
            onOpenDiagram={questionHasDiagram(q) || diagramAction(q) ? (optionId) => openDiagramFor(q, optionId) : undefined}
            onNewSearch={!isLocked ? (query, mode) => runSearch(q, query, mode) : undefined}
            canAddSearch={!!(onPartEdit || onNewSearch)}
            t={t}
          />
        )}
      </>
    );
  };

  // Under the options (list and diagram alike): the chat row, then the decline.
  const renderDecline = (q: ClarificationQuestion) => (
    <>
      {renderAsk(q)}
      {renderDeclineChoice(q)}
    </>
  );

  const renderDeclineChoice = (q: ClarificationQuestion) => {
    if (q.options.some(o => o.extra_details?._expert_action === 'show_diagram')) return null;
    if (q.question_type === 'free_text') return null;
    const noneChecked = (selections[q.id] ?? []).includes(NONE_OPTION_ID);
    // No longer OFFERED. "I can't find the part in the list" was a dead end
    // dressed as an answer — it dropped the term and the search had nothing new
    // to go on. The chat row above it now carries that job: describing the part
    // in your own words, or sending a photo, gives the search something to work
    // with. Kept visible only when a stored answer already holds the decline, so
    // an answered card still shows what was chosen.
    if (!noneChecked) return null;
    return (
      <>
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            padding: '7px 10px',
            fontSize: '13px',
            color: '#201e1a',
            backgroundColor: noneChecked ? '#ededff' : '#ffffff',
            border: `1px solid ${noneChecked ? PRIMARY : '#d6d5ff'}`,
            borderRadius: '8px',
            cursor: isLocked ? 'default' : 'pointer',
            lineHeight: '1.4',
            opacity: isAnswered && !noneChecked ? 0.4 : 1,
          }}
        >
          <input
            type={q.question_type === 'single_select' ? 'radio' : 'checkbox'}
            name={q.id}
            checked={noneChecked}
            disabled={isLocked}
            onChange={() => toggleNone(q.id)}
            style={{ marginTop: '2px', accentColor: PRIMARY, cursor: isLocked ? 'default' : 'pointer' }}
          />
          <span style={{ fontWeight: 600, color: '#6b6b6b' }}>{noneLabel}</span>
        </label>
        {/* Optional context. Declining alone drops the term; a line about what
            the part actually is gives the search something new to go on
            instead of re-asking. */}
        {noneChecked && (
          <input
            type="text"
            value={texts[q.id] ?? ''}
            onChange={e => setText(q.id, e.target.value)}
            disabled={isLocked}
            autoFocus
            placeholder={t('chat.clarification.partNotInListContext', {
              defaultValue: 'Optional: describe the part — where it sits, what it does, any number you have',
            })}
            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) onPrimaryClick(); }}
            style={{
              width: '100%',
              padding: '8px 11px',
              fontSize: '13px',
              color: '#201e1a',
              backgroundColor: '#ffffff',
              border: '1px solid #d6d5ff',
              borderRadius: '8px',
              outline: 'none',
              boxSizing: 'border-box',
              fontFamily: 'inherit',
            }}
            onFocus={e => { e.currentTarget.style.borderColor = PRIMARY; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(104,102,255,0.12)'; }}
            onBlur={e => { e.currentTarget.style.borderColor = '#d6d5ff'; e.currentTarget.style.boxShadow = 'none'; }}
          />
        )}
      </>
    );
  };

  // The text of a row: its name (catalog words in sentence case, never
  // shouting "CONJUNTO PANEL") and one grey line — the plain description and
  // any catalog notes. What sets it apart comes under it as the AI hint.
  const rowText = (q: ClarificationQuestion, opt: ClarificationQuestion['options'][number]): { title: string; line: string } => {
    const plain = plainLine(opt.description);
    if (q.parts_explained || isNarrowingQuestion(q)) return { title: plainWords(opt.label) || opt.label, line: plain };
    const ed = (opt.extra_details && typeof opt.extra_details === 'object') ? opt.extra_details as Record<string, unknown> : {};
    const NOTE_KEYS = ['remark', 'remarks', 'note', 'notes', 'optionalpack', 'optionalpackage'];
    const notes = Object.entries(ed)
      .filter(([k, v]) => NOTE_KEYS.includes(String(k).toLowerCase().replace(/[\s_-]/g, '')) && String(v ?? '').trim()
        && !['n/a', 'na', 'none', 'null', '-', '—'].includes(String(v).trim().toLowerCase()))
      .map(([, v]) => String(v).trim());
    // Rows that read the same ("Cylinder", "Cylinder") carry what tells them
    // apart — the backend attaches the OE number and the diagram area ONLY
    // when names collide, so showing them whenever present never clutters a
    // list whose names already differ.
    // Only customer words: the diagram area. Part numbers and diagram refs
    // are catalog identifiers — the customer cannot choose by them.
    const telling = ['Area']
      .map(k => plainWords(String(ed[k] ?? '')))
      .filter(Boolean);
    const label = plainWords(opt.label) || opt.label;
    const title = /[a-z]/.test(label) ? label : sentenceCase(label);
    return { title, line: Array.from(new Set([...telling, ...(plain ? [plain] : []), ...notes])).join(' · ') };
  };

  // The question as this card shows it, handed to the diagram viewer so its
  // question block reads the same: text and numbered answers (title, count,
  // grey line). A narrowing answer stands for catalog candidates — the map's
  // options — so the viewer can number each part by its answer.
  const displayQuestionFor = (q: ClarificationQuestion) => ({
    text: q.candidate_fit ? t('chat.clarification.notFoundRelatedQuestion', { defaultValue: 'Could one of these be the part you need?' }) : q.text,
    answers: rowOptions(q).map((o, i) => {
      const { title, line } = rowText(q, o);
      return { n: i + 1, title, line, count: o.candidate_count ?? null, optionIds: o.candidate_option_ids?.length ? o.candidate_option_ids : [o.id] };
    }),
    tagged: rowOptions(q).some(o => (o.candidate_option_ids?.length ?? 0) > 0),
  });

  const renderQuestion = (q: ClarificationQuestion) => {
        const selected = selections[q.id] ?? [];
        // Diagram questions present the section map as the primary selector.
        const hasDiagram = usesMap(q);
        const missing = q.missing?.length ? q.missing.join(' / ') : null;
        return (
          <div key={q.id} style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
            {/* A simple-choice question opens its EPC map only on request: a
                hidden map mount whose fullscreen modal is the whole UI, picks
                and Confirm included; unmounted when the modal closes. */}
            {opensMapOnDemand(q) && openRequests[q.id] && (
              <div data-testid="map-on-demand" style={{ display: 'none' }}>
                <ClarificationDiagramInline
                  question={mapQuestionFor(q)!}
                  headerLabel={headerLabels?.[q.id] || q.part_label || q.part_name}
                  displayQuestion={displayQuestionFor(q)}
                  // The AI comparison (side, finish, …) runs on the opened map
                  // like on any map question — it is what tells two identical
                  // catalog panels apart there.
                  onRequestHint={onRequestHint && !isAnswered && !isLocked ? requestAndSaveHint : undefined}
                  t={t}
                  questionType={mapQuestionFor(q)!.question_type}
                  selectedOptionIds={selected}
                  onToggleOption={(optionId) => toggleMulti(q.id, optionId)}
                  onSelectOther={(text, partKey) => selectOther(q, text, partKey)}
                  onClearOther={() => clearOther(q)}
                  otherSelected={selected.includes(OTHER_OPTION_ID)}
                  otherText={texts[q.id]}
                  otherKey={otherKeys[q.id]}
                  pickedPartIds={(pickedParts[q.id] ?? []).map(r => r.partId)}
                  priorPicks={priorPicksFor(q.id)}
                  onPickPart={(ref) => pickPart(q.id, ref, false)}
                  onUnpickPart={(partId) => unpickPart(q.id, partId)}
                  noneSelected={selected.includes(NONE_OPTION_ID)}
                  noneLabel={noneLabel}
                  disabled={isLocked}
                  onConfirm={onPrimaryClick}
                  confirmDisabled={primaryDisabled}
                  onClearSelection={() => clearSelection(q.id)}
                  onCantFind={!isLocked && chatAvailable(q) ? () => setChatFor(q.id) : undefined}
                  confirmLabel={mapConfirmLabel(q)}
                  openRequest={openRequests[q.id]}
                  onOpeningChange={(busy) => setMapOpening(prev => ({ ...prev, [q.id]: busy }))}
                  requestLine={requestLines?.[q.id]}
                  onFullscreenChange={(open) => {
                    if (!open) setOpenRequests(prev => { const next = { ...prev }; delete next[q.id]; return next; });
                  }}
                />
              </div>
            )}
            {/* Group header — the SAME label shown in the requested-parts list
                (resolved upstream), so the question reads identically. Rendered
                verbatim (no uppercasing) to match the list exactly. The Hint
                button sits on the same row, right-aligned, only while the
                question is still answerable and has candidates to compare. */}
            <span style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              {(missing || isAnswered) && <span
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: missing ? '#92610a' : PRIMARY,
                  letterSpacing: missing ? '0.06em' : '0.01em',
                  textTransform: missing ? 'uppercase' : 'none',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {missing
                  ? t('chat.clarification.missing', { defaultValue: 'Missing · {{what}}', what: missing })
                  : (headerLabels?.[q.id] || q.part_label || q.part_name)}
              </span>}
              {/* List-only questions get the Hint here; diagram questions carry
                  it in the parts list beside the rows it explains. */}
              {hintAvailable(q) && !hasDiagram && (
                <HintButton t={t} loading={hints[q.id]?.status === 'loading'} onClick={() => openHint(q)} />
              )}
              {/* The note opens as a bubble under this row (it runs long); the
                  map has its own mount for diagram questions. */}
              {!hasDiagram && hintOpen[q.id] && hints[q.id] && (
                <HintBubble
                  t={t}
                  question={q}
                  state={hints[q.id]}
                  onClose={() => closeHint(q.id)}
                  onFocusOption={(optionId) => focusOption(q.id, optionId)}
                />
              )}
            </span>
            {/* Question text — already localized server-side, render verbatim */}
            <p style={{ margin: '0 0 2px', fontSize: '19px', fontWeight: 700, color: '#282631', lineHeight: '1.4', minWidth: 0, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{q.candidate_fit ? t('chat.clarification.notFoundRelatedQuestion', { defaultValue: 'Could one of these be the part you need?' }) : q.text}</p>


            {q.question_type === 'free_text' ? (
              <input
                type="text"
                value={texts[q.id] ?? ''}
                onChange={e => setText(q.id, e.target.value)}
                disabled={isLocked}
                placeholder={t('chat.clarification.typeAnswer', { defaultValue: 'Type your answer' })}
                onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) onPrimaryClick(); }}
                style={{
                  width: '100%',
                  padding: '8px 11px',
                  fontSize: '13px',
                  color: '#201e1a',
                  backgroundColor: '#ffffff',
                  border: '1px solid #d6d5ff',
                  borderRadius: '8px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                }}
                onFocus={e => { e.currentTarget.style.borderColor = PRIMARY; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(104,102,255,0.12)'; }}
                onBlur={e => { e.currentTarget.style.borderColor = '#d6d5ff'; e.currentTarget.style.boxShadow = 'none'; }}
              />
            ) : hasDiagram ? (
              <>
                {/* The exploded-view map: open it to check a chip against the
                    drawing, click a highlighted part, or a grayed (non-related)
                    part to choose your own. */}
                <ClarificationDiagramInline
                  question={q}
                  headerLabel={headerLabels?.[q.id] || q.part_label || q.part_name}
                  displayQuestion={displayQuestionFor(q)}
                  onRequestHint={hintAvailable(q) ? requestAndSaveHint : undefined}
                  t={t}
                  questionType={q.question_type}
                  selectedOptionIds={selected}
                  onToggleOption={(optionId) => toggleMulti(q.id, optionId)}
                  onSelectOther={(text, partKey) => selectOther(q, text, partKey)}
                  onClearOther={() => clearOther(q)}
                  otherSelected={selected.includes(OTHER_OPTION_ID)}
                  otherText={texts[q.id]}
                  otherKey={otherKeys[q.id]}
                  pickedPartIds={(pickedParts[q.id] ?? []).map(r => r.partId)}
                  priorPicks={priorPicksFor(q.id)}
                  onPickPart={(ref) => pickPart(q.id, ref, false)}
                  onUnpickPart={(partId) => unpickPart(q.id, partId)}
                  // Undo only — see renderDeclineChoice. The map's button is
                  // guarded by `onSelectNone &&`, so passing undefined removes it.
                  onSelectNone={selected.includes(NONE_OPTION_ID) ? () => { if (!isLocked) toggleNone(q.id); } : undefined}
                  noneSelected={selected.includes(NONE_OPTION_ID)}
                  noneLabel={noneLabel}
                  disabled={isLocked}
                  onConfirm={onPrimaryClick}
                  confirmDisabled={primaryDisabled}
                  onClearSelection={() => clearSelection(q.id)}
                  onCantFind={!isLocked && chatAvailable(q) ? () => setChatFor(q.id) : undefined}
                  confirmLabel={mapConfirmLabel(q)}
                  openFullscreenOnMount={fullscreenFlow}
                  openRequest={openRequests[q.id] ?? null}
                  requestLine={requestLines?.[q.id]}
                  onFullscreenChange={setFullscreenFlow}
                  stepper={isStepper ? { current: step + 1, total: questions.length, isLast: onLastStep, canGoBack: step > 0, onBack: goBack } : undefined}
                />
                {/* The decline lives under the map too. Inside the map it is a
                    bare toggle with nowhere to type; here it opens the context
                    box, so "not in the list" is never a dead end. */}
                {renderDecline(q)}
              </>
            ) : (
              <>
                {q.question_type !== 'single_select' && (
                  <span style={{ fontSize: '12px', color: '#6c6978' }}>
                    {t('chat.clarification.selectAll', { defaultValue: 'Select all that apply' })}
                  </span>
                )}
                {/* Tied candidates: what the photo showed, side by side (Stage 3b). */}
                <MatchEvidenceCompare options={rowOptions(q)} decidingDifference={q.deciding_difference} />
                <div className="stagger" style={{ display: 'flex', flexDirection: 'column' }}>
                  {/* Numbered rows: a single-select row IS the answer (click or
                      press its number); multi-select rows toggle, then Submit.
                      Navigation actions ("Show diagram") are the footer button. */}
                  {rowOptions(q).map((opt, i) => {
                    const isChecked = selected.includes(opt.id);
                    const flashKey = `${q.id}:${opt.id}`;
                    const isFlashed = flashOption === flashKey;
                    return (
                      <label
                        key={opt.id}
                        ref={(el) => { if (el) optionRefs.current.set(flashKey, el); else optionRefs.current.delete(flashKey); }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '20px',
                          padding: '14px 8px',
                          boxSizing: 'border-box',
                          fontSize: '13px',
                          color: '#201e1a',
                          backgroundColor: isChecked || isFlashed ? '#f5f4ff' : 'transparent',
                          borderBottom: '1px solid #ececf1',
                          borderRadius: isFlashed ? '8px' : 0,
                          cursor: isLocked ? 'default' : 'pointer',
                          lineHeight: '1.4',
                          opacity: isAnswered && !isChecked ? 0.4 : 1,
                          transition: 'background-color 0.2s ease',
                        }}
                      >
                        <input
                          type={q.question_type === 'single_select' ? 'radio' : 'checkbox'}
                          name={q.id}
                          checked={isChecked}
                          disabled={isLocked}
                          onChange={() =>
                            q.question_type === 'single_select'
                              ? applyChoice(q, opt.id)
                              : toggleMulti(q.id, opt.id)
                          }
                          style={{ position: 'absolute', opacity: 0, width: 1, height: 1, pointerEvents: 'none' }}
                        />
                        <span aria-hidden="true" style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 600, color: isChecked ? '#ffffff' : '#4a4856', backgroundColor: isChecked ? PRIMARY : '#f1f0f6' }}>
                          {i + 1}
                        </span>
                        <span style={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
                          {/* ONE structure for every row of every question:
                              a plain name in bold, one grey line under it, the
                              AI hint below. No match bars or extra chips. */}
                          {(() => {
                            const { title, line } = rowText(q, opt);
                            return (
                              <>
                                <span style={{ fontSize: '15px', fontWeight: 700, color: '#282631' }}>
                                  {title}
                                  {opt.suggested && (
                                    <span data-testid="choice-suggested" style={{ marginLeft: 8, padding: '1px 8px', borderRadius: 999, fontSize: '12px', fontWeight: 600, color: '#0f6a56', backgroundColor: '#e6f4ef', verticalAlign: 'middle' }}>
                                      {t('chat.clarification.chat.suggested', { defaultValue: 'Suggested' })}
                                    </span>
                                  )}
                                  {typeof opt.candidate_count === 'number' && opt.candidate_count > 0 && (
                                    <span data-testid="choice-count" style={{ marginLeft: 8, fontSize: '12.5px', fontWeight: 500, color: '#6c6978' }}>
                                      {opt.candidate_count === 1
                                        ? t('chat.clarification.onePart', { defaultValue: '1 part' })
                                        : t('chat.clarification.nParts', { defaultValue: '{{count}} parts', count: opt.candidate_count })}
                                    </span>
                                  )}
                                </span>
                                {line && (
                                  <span data-testid="row-line" style={{ fontSize: '13.5px', color: '#6c6978', lineHeight: 1.4 }}>{line}</span>
                                )}
                                {/* The AI hint: side / position and what sets it apart. */}
                                {(hintAvailable(q) || rowHints(q)) && <HintCandidateNote t={t} state={hints[q.id]} question={q} optionId={opt.id} />}
                              </>
                            );
                          })()}
                        </span>
                      </label>
                    );
                  })}

                  {/* "Type your own" escape hatch — same control type as the options.
                      Replaced by the chat box ("Something else — describe it in
                      your words or ask…") wherever that is available. */}
                  {!chatAvailable(q) && (() => {
                    const otherChecked = selected.includes(OTHER_OPTION_ID);
                    return (
                      <>
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '8px',
                            padding: '7px 10px',
                            fontSize: '13px',
                            color: '#201e1a',
                            backgroundColor: otherChecked ? '#ededff' : '#ffffff',
                            border: `1px solid ${otherChecked ? PRIMARY : '#d6d5ff'}`,
                            borderRadius: '8px',
                            cursor: isLocked ? 'default' : 'pointer',
                            lineHeight: '1.4',
                            opacity: isAnswered && !otherChecked ? 0.4 : 1,
                          }}
                        >
                          <input
                            type={q.question_type === 'single_select' ? 'radio' : 'checkbox'}
                            name={q.id}
                            checked={otherChecked}
                            disabled={isLocked}
                            onChange={() =>
                              q.question_type === 'single_select'
                                ? toggleSingle(q.id, OTHER_OPTION_ID)
                                : toggleMulti(q.id, OTHER_OPTION_ID)
                            }
                            style={{ marginTop: '2px', accentColor: PRIMARY, cursor: isLocked ? 'default' : 'pointer' }}
                          />
                          <span style={{ fontWeight: 600 }}>
                            {t('chat.clarification.typeYourOwn', { defaultValue: 'Type your own' })}
                          </span>
                        </label>
                        {otherChecked && (
                          <input
                            type="text"
                            value={texts[q.id] ?? ''}
                            onChange={e => setText(q.id, e.target.value)}
                            disabled={isLocked}
                            autoFocus
                            placeholder={t('chat.clarification.typeAnswer', { defaultValue: 'Type your answer' })}
                            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) onPrimaryClick(); }}
                            style={{
                              width: '100%',
                              padding: '8px 11px',
                              fontSize: '13px',
                              color: '#201e1a',
                              backgroundColor: '#ffffff',
                              border: '1px solid #d6d5ff',
                              borderRadius: '8px',
                              outline: 'none',
                              boxSizing: 'border-box',
                              fontFamily: 'inherit',
                            }}
                            onFocus={e => { e.currentTarget.style.borderColor = PRIMARY; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(104,102,255,0.12)'; }}
                            onBlur={e => { e.currentTarget.style.borderColor = '#d6d5ff'; e.currentTarget.style.boxShadow = 'none'; }}
                          />
                        )}
                      </>
                    );
                  })()}

                  {renderDecline(q)}
                </div>
              </>
            )}
          </div>
        );
  };

  // On the last step (or single/locked form) the action is the real Submit; on
  // an earlier step it advances to the next question. Submit stays gated on ALL
  // questions being answered, Next only on the current one.
  const onLastStep = !isStepper || step === lastStep;
  const primaryDisabled = onLastStep
    ? !allAnswered || isLocked
    : !isLocked && !isQuestionAnswered(currentQuestion);
  const primaryLabel = onLastStep
    ? t('chat.clarification.submit', { defaultValue: 'Submit' })
    : t('chat.clarification.next', { defaultValue: 'Next' });
  const onPrimaryClick = onLastStep ? handleSubmit : goNext;
  // The diagram's Confirm names how many parts it sends: "Confirm 2 parts".
  const pickCount = (q: ClarificationQuestion): number => {
    const sel = selections[q.id] ?? [];
    if (sel.includes(NONE_OPTION_ID)) return 0;
    const real = sel.filter(id => id !== OTHER_OPTION_ID).length;
    const typed = sel.includes(OTHER_OPTION_ID) && (texts[q.id] ?? '').trim() ? 1 : 0;
    return real + (pickedParts[q.id]?.length ?? 0) + typed;
  };
  const mapConfirmLabel = (q: ClarificationQuestion): string => {
    const n = pickCount(q);
    if (onLastStep) {
      if (n > 1) return t('chat.clarification.confirmNParts', { defaultValue: 'Confirm {{count}} parts', count: n });
      return n === 1
        ? t('chat.clarification.confirmSelectedPart', { defaultValue: 'Confirm this part' })
        : t('chat.clarification.confirm', { defaultValue: 'Confirm' });
    }
    return n > 1
      ? t('chat.clarification.saveNPartsAndContinue', { defaultValue: 'Save {{count}} parts and continue', count: n })
      : t('chat.clarification.saveAndContinue', { defaultValue: 'Save and continue' });
  };


  // Answered → collapse to a compact summary bar; click to expand and review the
  // locked picks.
  // Earlier questions of this card, as numbered steps the customer can move
  // between: a step opens that question as it was answered (read-only);
  // "Back" returns to the current one. Answers are not changed from here —
  // the current question stays the one to answer.
  const stepRows = (previousSteps || []).flatMap((step, si) => step.questions.map(q => ({
    key: `${si}:${q.id}`, question: q, answer: step.answers?.[q.id] ?? null, requestLine: step.requestLines?.[q.id],
  }))).filter(r => r.question.text);
  // The part the customer originally asked for — their own words when we
  // have them, else our label for the line — so each answered row keeps
  // saying which request it narrowed.
  const requestedPartOf = (q: ClarificationQuestion, line?: { original: string; label: string }): string =>
    (line?.original || '').trim() || (line?.label || '').trim() || headerLabels?.[q.id] || q.part_label || q.part_name || '';
  type StepRowSpec = { key: string; text: string; answer: string; request?: string; active: boolean; onClick: () => void; testId: string };
  const renderPreviousSteps = (extra: StepRowSpec[] = []) => {
    // One row per answered question — ✓, the question, the answer — all
    // alike; a click opens it, a second click (or "Back") closes it.
    const rows: StepRowSpec[] = [
      ...stepRows.map((r, i) => ({
        key: r.key, text: r.question.text, request: requestedPartOf(r.question, r.requestLine),
        answer: answerWords(r.question, r.answer) || t('chat.clarification.stepN', { defaultValue: 'Question {{n}}', n: i + 1 }),
        active: viewStep === i, onClick: () => setViewStep(viewStep === i ? null : i), testId: 'step-pill',
      })),
      ...extra,
    ];
    if (!rows.length) return null;
    return (
      <div data-testid="previous-steps" role="tablist" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {rows.map((r, i) => (
          <React.Fragment key={r.key}>
            <button type="button" role="tab" aria-selected={r.active} data-testid={r.testId} onClick={r.onClick}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '7px 10px', borderRadius: 8, border: 'none', background: r.active ? '#f5f4ff' : 'transparent', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', minWidth: 0 }}>
              <span aria-hidden="true" style={{ flexShrink: 0, width: 18, height: 18, borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#d8f3ea', color: ANSWERED_GREEN, fontSize: 11, fontWeight: 800 }}>✓</span>
              <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {r.request && (
                  <span data-testid="step-request" style={{ fontSize: '11.5px', fontWeight: 700, color: '#3627BF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>“{r.request}”</span>
                )}
                <span style={{ fontSize: '13px', color: '#6c6978', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.text}</span>
              </span>
              <strong style={{ flexShrink: 0, maxWidth: '45%', fontSize: '13.5px', color: '#282631', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.answer}</strong>
            </button>
            {i < stepRows.length && r.active && renderStepView(i)}
          </React.Fragment>
        ))}
      </div>
    );
  };
  // An earlier question, as it was asked and answered.
  const renderStepView = (stepIndex = viewStep) => {
    if (stepIndex === null || !stepRows[stepIndex]) return null;
    const { question: q, answer } = stepRows[stepIndex];
    const picked = new Set(answer?.selected_option_ids || []);
    const rows = q.options.filter(o => !o.extra_details?._expert_action);
    const onRow = rows.some(o => picked.has(o.id));
    return (
      <div data-testid="step-view" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: '17px', fontWeight: 700, color: '#282631', lineHeight: 1.4 }}>{q.text}</span>
        {rows.map((opt, i) => {
          const on = picked.has(opt.id);
          return (
            <div key={opt.id} style={{ display: 'flex', alignItems: 'center', gap: 20, padding: '10px 8px', borderBottom: '1px solid #ececf1', opacity: on ? 1 : 0.45 }}>
              <span aria-hidden="true" style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 700, color: on ? '#ffffff' : '#4a4856', backgroundColor: on ? ANSWERED_GREEN : '#f1f0f6' }}>
                {on ? '✓' : i + 1}
              </span>
              <span style={{ fontSize: '15px', fontWeight: 700, color: '#282631', overflowWrap: 'anywhere' }}>{opt.label}</span>
            </div>
          );
        })}
        {!onRow && answerWords(q, answer) && (
          <span style={{ fontSize: '14px', fontWeight: 700, color: '#282631' }}>✓ {answerWords(q, answer)}</span>
        )}
      </div>
    );
  };

  // Answered and the next question is on its way: the question stays open as
  // it was (the pick marked) under a loading overlay — collapsing it to one
  // line and then growing the next question made the card jump.
  if (isAnswered && !waitingNext) {
    // The request line a question is about: “original” → our reading. In the
    // header when there is one question; on each Q/A block when there are more.
    const requestLineOf = (q: ClarificationQuestion): React.ReactNode => {
      const pair = requestLines?.[q.id];
      const showPair = !!pair && pair.original.trim() !== '' && pair.original.trim().toLowerCase() !== pair.label.trim().toLowerCase();
      return showPair
        ? <>“{pair!.original}” <span aria-hidden="true">→</span> {pair!.label}</>
        : (headerLabels?.[q.id] || q.part_label || q.part_name);
    };
    const q0 = questions.length === 1 ? questions[0] : null;
    const allSkipped = questions.every(q => skippedQ[q.id]);
    // What was chosen for a question, as the parts it resolved to.
    type Chosen = { key: string; callout: string; name: string; partNumber: string; optionId?: string };
    const chosenFor = (q: ClarificationQuestion): Chosen[] => {
      const out: Chosen[] = [];
      for (const id of selections[q.id] ?? []) {
        if (id === OTHER_OPTION_ID || id === NONE_OPTION_ID) continue;
        const opt = findOption(q, id);
        if (!opt) continue;
        const ref = opt.load?.[0];
        out.push({ key: `o:${id}`, callout: String(ref?.callout ?? ''), name: ref?.part_name || opt.label, partNumber: String(ref?.part_number ?? ''), optionId: id });
      }
      for (const r of pickedParts[q.id] ?? []) {
        out.push({ key: `p:${r.partId}`, callout: String(r.callout ?? ''), name: r.part_name || String(r.partId), partNumber: String((r as any).part_number ?? '') });
      }
      return out;
    };
    const answerText = (q: ClarificationQuestion): string => {
      if (skippedQ[q.id]) return skipLabel;
      if (q.question_type === 'free_text') return (texts[q.id] ?? '').trim();
      const sel = selections[q.id] ?? [];
      if (sel.includes(NONE_OPTION_ID)) {
        const context = (texts[q.id] ?? '').trim();
        return context ? `${noneLabel} — ${context}` : noneLabel;
      }
      const parts = chosenFor(q).map(c => c.callout ? `${sentenceCase(c.name)} (${c.callout})` : sentenceCase(c.name));
      const typed = (texts[q.id] ?? '').trim();
      if (sel.includes(OTHER_OPTION_ID) && typed) parts.push(typed);
      return parts.join(', ');
    };
    // The answer as the customer picked it — "Left", "Front bumper", the
    // typed words — for the collapsed line.
    const shortAnswer = (q: ClarificationQuestion): string => {
      if (skippedQ[q.id]) return skipLabel;
      if (q.question_type === 'free_text') return (texts[q.id] ?? '').trim();
      const sel = selections[q.id] ?? [];
      if (sel.includes(NONE_OPTION_ID)) return noneLabel;
      const out = sel
        .filter(id => id !== OTHER_OPTION_ID && id !== NONE_OPTION_ID)
        .map(id => findOption(q, id)?.label || '')
        .filter(Boolean);
      for (const r of pickedParts[q.id] ?? []) out.push(sentenceCase(r.part_name || String(r.partId)));
      const typed = (texts[q.id] ?? '').trim();
      if (sel.includes(OTHER_OPTION_ID) && typed) out.push(typed);
      return out.join(', ');
    };
    const aiTags = (q: ClarificationQuestion, optionId?: string): string[] => {
      if (!optionId) return [];
      const note = (savedGuidance.current[q.id] || []).find(n => n.candidate.option_id === optionId);
      return (note?.candidate.tags || []).slice(0, 2);
    };
    // "View on diagram" for one question — in the green header on a single-
    // question card, next to its own question when several share the card.
    const viewButton = (q: ClarificationQuestion, chosen: Chosen[]) => {
      if (q.question_type === 'free_text' || !questionHasDiagram(q) || chosen.length === 0) return null;
      return (
        <button
          type="button"
          onClick={() => {
            const first = chosen.find(c => c.optionId) ?? chosen[0];
            setOpenRequests(prev => ({ ...prev, [q.id]: { optionId: first.optionId || '', nonce: Date.now() } }));
          }}
          style={{ flexShrink: 0, marginLeft: 'auto', border: `1px solid ${PRIMARY}`, background: '#ffffff', color: PRIMARY, borderRadius: 999, padding: '4px 11px', fontSize: '12px', fontWeight: 600, lineHeight: 1.5, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
        >{t('chat.clarification.viewOnDiagram', { defaultValue: 'View on diagram' })}</button>
      );
    };
    const badge = (label: string, green: boolean) => (
      <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: green ? ANSWERED_GREEN : '#6c6978', background: green ? '#d8f3ea' : '#eeedf3' }}>{label}</span>
    );
    return (
      <div className="card-enter" style={{ marginTop: '10px', backgroundColor: '#ffffff', border: '1px solid #e3e2e8', borderRadius: '12px', overflow: 'hidden' }}>
        {stepRows.length ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '10px 12px', borderBottom: answeredOpen ? '1px solid #ececf1' : undefined }}>
            {renderPreviousSteps(questions.map(q => ({
              key: `now:${q.id}`, text: q.text, request: requestedPartOf(q, requestLines?.[q.id]), answer: shortAnswer(q), active: answeredOpen, testId: 'answered-toggle',
              onClick: () => { setViewStep(null); setAnsweredOpen(o => !o); },
            })))}
          </div>
        ) : null}
        {/* Green header, one line: status, the line it answered, and — for a
            single-question card — the way back to its diagram. Not on a card
            with earlier steps: there the answer is one more row above. */}
        {!stepRows.length && <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 12px 8px 10px', background: allSkipped ? '#fdf6e3' : '#eefaf6', borderLeft: `4px solid ${allSkipped ? SKIPPED_AMBER : ANSWERED_GREEN}`, borderBottom: `1px solid ${allSkipped ? SKIPPED_AMBER : ANSWERED_GREEN}33` }}>
          <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '11px', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: allSkipped ? SKIPPED_AMBER : ANSWERED_GREEN, background: allSkipped ? '#fbecc4' : '#d8f3ea', borderRadius: 999, padding: '3px 10px' }}>
            {allSkipped
              ? <>⏭ {t('chat.clarification.skippedChip', { defaultValue: 'Skipped' })}</>
              : <>✓ {t('chat.clarification.answered', { defaultValue: 'Answered' })}</>}
          </span>
          <span style={{ flex: '1 1 180px', minWidth: 0, fontSize: '13px', fontWeight: 700, color: '#0f6a56', lineHeight: 1.35, overflowWrap: 'anywhere' }}>
            {/* The question itself, so the collapsed card still says what was
                asked ("Is it the front or the rear Door?" → Both). */}
            {q0
              ? <span data-testid="answered-question" style={{ color: '#282631', fontWeight: 600 }}>{q0.text || requestLineOf(q0)}</span>
              : t('chat.clarification.questionsAnswered', { defaultValue: '{{count}} clarification questions answered', count: questions.length })}
          </span>
          {/* The answer itself, then the way back to the full question. */}
          <span data-testid="answered-summary" style={{ minWidth: 0, fontSize: '13.5px', fontWeight: 700, color: '#282631', overflowWrap: 'anywhere' }}>
            {questions.map(shortAnswer).filter(Boolean).join(' · ')}
          </span>
          <button
            type="button"
            data-testid="answered-toggle"
            aria-expanded={answeredOpen}
            onClick={() => setAnsweredOpen(o => !o)}
            style={{ flexShrink: 0, marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'transparent', padding: '2px 0', color: '#3627BF', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            {answeredOpen
              ? t('chat.clarification.hideQuestion', { defaultValue: 'Hide question' })
              : t('chat.clarification.showQuestion', { defaultValue: 'Show question' })}
            <ChevronDown size={14} aria-hidden="true" style={{ transform: answeredOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }} />
          </button>
        </div>}
        {/* Reopened: Q / A per question, then the part(s) the answer resolved
            to, and "View on diagram". */}
        {answeredOpen && q0 && viewButton(q0, chosenFor(q0)) && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 12px 0' }}>{viewButton(q0, chosenFor(q0))}</div>
        )}
        {answeredOpen && <div data-testid="answered-details" style={{ display: 'flex', flexDirection: 'column' }}>
          {questions.map((q, qi) => {
            const chosen = chosenFor(q);
            return (
              <div key={q.id} style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 12px', borderTop: qi > 0 ? '1px dashed #e3e2e8' : undefined }}>
                {questions.length > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ flex: '1 1 180px', minWidth: 0, fontSize: '12px', fontWeight: 700, color: '#0f6a56', lineHeight: 1.4, overflowWrap: 'anywhere' }}>
                      {requestLineOf(q)}
                    </span>
                    {viewButton(q, chosen)}
                  </div>
                )}
                {/* The question as it was asked: its text, then every choice
                    — the picked one ticked, the rest dimmed. */}
                <span style={{ fontSize: '15px', fontWeight: 700, color: '#282631', lineHeight: 1.4, overflowWrap: 'anywhere' }}>{q.text}</span>
                {q.question_type !== 'free_text' && rowOptions(q).length > 0 && (
                  <div data-testid="answered-options" style={{ display: 'flex', flexDirection: 'column' }}>
                    {rowOptions(q).map((opt, i) => {
                      const picked = (selections[q.id] ?? []).includes(opt.id);
                      return (
                        <div key={opt.id} data-picked={picked ? 'true' : undefined}
                          style={{ display: 'flex', alignItems: 'center', gap: 20, padding: '10px 8px', borderBottom: '1px solid #ececf1', opacity: picked ? 1 : 0.45 }}>
                          <span aria-hidden="true" style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 700, color: picked ? '#ffffff' : '#4a4856', backgroundColor: picked ? ANSWERED_GREEN : '#f1f0f6' }}>
                            {picked ? '✓' : i + 1}
                          </span>
                          <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                            <span style={{ fontSize: '15px', fontWeight: 700, color: '#282631', overflowWrap: 'anywhere' }}>{opt.label}</span>
                            {plainLine(opt.description) && (
                              <span style={{ fontSize: '13.5px', color: '#6c6978', lineHeight: 1.4 }}>{plainLine(opt.description)}</span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* An answer that is not one of the rows: typed words, "not in
                    the list", or a part picked on the diagram. */}
                {(() => {
                  const rowIds = new Set(rowOptions(q).map(o => o.id));
                  const extra = chosen.filter(c => !c.optionId || !rowIds.has(c.optionId));
                  const anyRowPicked = (selections[q.id] ?? []).some(id => rowIds.has(id));
                  if (extra.length === 0) {
                    return anyRowPicked ? null : (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        {badge('A', true)}
                        <span style={{ fontSize: '14px', fontWeight: 700, color: '#282631', lineHeight: 1.45, minWidth: 0, overflowWrap: 'anywhere' }}>{answerText(q)}</span>
                      </div>
                    );
                  }
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {extra.map(c => {
                        const tags = aiTags(q, c.optionId);
                        return (
                          <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            {c.callout && <DiagramRefChip number={c.callout} priority="hero" />}
                            <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                              <span style={{ fontSize: '13.5px', fontWeight: 700, color: '#282631', lineHeight: 1.3, overflowWrap: 'anywhere' }}>{c.name}</span>
                              {c.partNumber && <span style={{ fontSize: '13px', fontWeight: 700, color: '#282631', letterSpacing: '0.02em' }}>{c.partNumber}</span>}
                            </span>
                            {tags.length > 0 && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '12px', fontWeight: 600, color: '#3627BF', background: '#eef0ff', border: '1px solid #d6d8ff', borderRadius: 999, padding: '4px 10px', overflowWrap: 'anywhere' }}>
                                ✦ {tags.join(' · ')}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>}
        {/* "View on diagram": a hidden map component whose only job is to open
            the fullscreen modal on the chosen part; unmounted when it closes. */}
        {questions.filter(q => openRequests[q.id]).map(q => (
          <div key={`view:${q.id}`} style={{ display: 'none' }}>
            <ClarificationDiagramInline
              question={q}
              headerLabel={headerLabels?.[q.id] || q.part_label || q.part_name}
              // The diagram opened from a row still tells its parts apart with
              // the AI tags, like every other map.
              onRequestHint={onRequestHint && !isAnswered && !isLocked ? requestAndSaveHint : undefined}
              t={t}
              questionType={q.question_type}
              selectedOptionIds={selections[q.id] ?? []}
              onToggleOption={() => {}}
              onSelectOther={() => {}}
              onClearOther={() => {}}
              pickedPartIds={(pickedParts[q.id] ?? []).map(r => r.partId)}
              priorPicks={[]}
              onPickPart={() => {}}
              onUnpickPart={() => {}}
              noneSelected={(selections[q.id] ?? []).includes(NONE_OPTION_ID)}
              noneLabel={noneLabel}
              disabled
              onConfirm={() => {}}
              confirmDisabled
              openRequest={openRequests[q.id]}
              requestLine={requestLines?.[q.id]}
              answered
              onFullscreenChange={(open) => {
                if (!open) setOpenRequests(prev => { const next = { ...prev }; delete next[q.id]; return next; });
              }}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className="card-enter"
      // Locked because the viewer is not the owner: a click on any option,
      // diagram row or disabled control offers to take the order instead of
      // silently doing nothing. Portaled map clicks bubble here too.
      onClickCapture={(e) => {
        if (!disabled || isAnswered || !promptTakeOrder) return;
        const el = (e.target as HTMLElement).closest?.('label, [aria-disabled="true"], button[disabled], input[disabled]');
        if (!el) return;
        e.preventDefault();
        e.stopPropagation();
        promptTakeOrder();
      }}
      style={{
        position: 'relative',
        marginTop: '10px',
        backgroundColor: '#ffffff',
        border: '1px solid #e3e2e8',
        borderRadius: '12px',
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
      }}
    >
      {disabled && lockedNote && (
        <div
          role="note"
          style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '-12px -14px 0', padding: '8px 14px', borderRadius: '12px 12px 0 0', background: '#fff7e0', color: '#8A5A00', fontSize: 12, fontWeight: 600, borderBottom: '1px solid #f3e2b3' }}
        >
          {lockedNote}
        </div>
      )}
      {/* Card header: the amber "Clarification needed" pill, then the line
          being asked about as the customer typed it → our reading of it, and
          how many catalog candidates are behind the question. The question
          itself is the heading under it (renderQuestion). */}
      {(() => {
        const q0 = visibleQuestions[0];
        const pair = q0 ? requestLines?.[q0.id] : undefined;
        const showPair = !!pair && pair.original.trim() !== '' && pair.original.trim().toLowerCase() !== pair.label.trim().toLowerCase();
        // "Door" → "Door, Front" once "Front" was answered.
        const cleanLabel = q0 ? refinedRequestLabel(
          showPair ? pair!.label : (headerLabels?.[q0.id] || q0.part_label || q0.part_name), q0.narrowing_trail) : '';
        const fit = q0?.candidate_fit;
        // Planner metadata rides on the one question asked this turn.
        const planStep = q0?.plan_step ?? null;
        const matches = q0 && q0.question_type !== 'free_text' ? (q0.candidate_total || matchCount(q0)) : 0;
        const trail = (q0?.narrowing_trail || []).filter(s => s.to < s.from);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '11px', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#92610a', backgroundColor: '#fde68a', borderRadius: 999, padding: '4px 11px' }}>
                <span aria-hidden="true">⚠</span>
                {t('chat.clarification.needed', { defaultValue: 'Clarification needed' })}
              </span>
              {planStep && planStep.total > 1 && (
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#6c6978' }}>
                  {t('chat.clarification.planStep', { defaultValue: 'Question {{current}} of {{total}}', current: planStep.current, total: planStep.total })}
                </span>
              )}
            </span>
            {/* The questions already answered, then the part request this
                question is about — right above the question, so it always
                reads in its context. */}
            {renderPreviousSteps()}
            {q0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 14px', backgroundColor: '#f4f4f7', border: '1px solid #ececf1', borderRadius: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', minHeight: 30 }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6c6978' }}>
                    {t('chat.clarification.partRequest', { defaultValue: 'Part request' })}
                  </span>
                  <span style={{ flex: '1 1 160px', minWidth: 0, fontSize: '14px', color: '#282631', lineHeight: 1.4, overflowWrap: 'anywhere' }}>
                    {showPair
                      ? <><em style={{ color: '#4a4856' }}>“{pair!.original}”</em> <span aria-hidden="true" style={{ color: '#6c6978', margin: '0 4px' }}>→</span> <strong>{cleanLabel}</strong></>
                      : <strong>{cleanLabel}</strong>}
                  </span>
                  {matches > 1 && (
                    <span data-testid="candidate-count" style={{ fontSize: '13px', color: '#6c6978', whiteSpace: 'nowrap' }}>
                      {t('chat.clarification.candidateCount', { defaultValue: '{{count}} matches', count: matches })}
                    </span>
                  )}
                  {trail.length > 0 && (
                    <button type="button" data-testid="request-details" onClick={() => setDetailsOpen(o => !o)} aria-expanded={detailsOpen}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'transparent', padding: 0, color: '#3627BF', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                      {t('chat.clarification.details', { defaultValue: 'Details' })}
                      <ChevronDown size={14} aria-hidden="true" style={{ transform: detailsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }} />
                    </button>
                  )}
                </div>
                {/* Every answer so far and how it cut the list: Front 24 → 12 · Left 12 → 3 */}
                {detailsOpen && trail.length > 0 && (
                  <span data-testid="narrowing-trail" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingBottom: 2 }}>
                    {trail.map((s, i) => (
                      <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '12px', color: '#137F67', background: '#e6f5f1', borderRadius: 999, padding: '3px 10px' }}>
                        <span aria-hidden="true">✓</span>
                        <strong style={{ fontWeight: 700 }}>{s.label}</strong>
                        <span style={{ color: '#4a4856' }}>{s.from} → {s.to}</span>
                        {s.carried_from && (
                          <span data-testid="carried-from" style={{ color: '#4a4856' }}>
                            · {t('chat.clarification.sameAs', { defaultValue: 'same as {{part}}', part: s.carried_from })}
                          </span>
                        )}
                      </span>
                    ))}
                  </span>
                )}
              </div>
            )}
            {fit && (
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#92610a', lineHeight: 1.45 }}>
                {t('chat.clarification.notFoundRelated', { defaultValue: 'I couldn’t find “{{part}}” in this catalog', part: cleanLabel })}
                {' '}{t('chat.clarification.notFoundRelatedPick', { defaultValue: 'Here are the options we found.' })} {fit.note}
              </span>
            )}
            {/* The lead ("Thanks — that narrows it down…") is not shown: the
                question itself says what is asked. */}          </div>
        );
      })()}
      {/* Answered overlay — once submitted/reloaded-answered, dim the whole form
          and stamp it as answered so it clearly reads as locked/done. */}
      {isAnswered && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 5,
            borderRadius: '12px',
            background: 'rgba(243,242,255,0.62)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: '16px',
            pointerEvents: 'none',
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              fontWeight: 700,
              color: PRIMARY,
              backgroundColor: '#ffffff',
              padding: '6px 14px',
              borderRadius: '999px',
              letterSpacing: '0.03em',
              textTransform: 'uppercase',
              border: `1px solid ${PRIMARY}33`,
              boxShadow: '0 4px 14px rgba(104,102,255,0.18)',
            }}
          >
            ✓ {t('chat.clarification.answered', { defaultValue: 'Answered' })}
          </span>
        </div>
      )}

      {/* Step progress — only when walking multiple questions one at a time */}
      {isStepper && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: PRIMARY }}>
            {t('chat.clarification.stepProgress', {
              defaultValue: 'Question {{current}} of {{total}}',
              current: step + 1,
              total: questions.length,
            })}
          </span>
          <div style={{ display: 'flex', gap: '5px' }}>
            {questions.map((q, i) => (
              <span
                key={q.id}
                style={{
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  backgroundColor:
                    i === step ? PRIMARY : isQuestionAnswered(q) ? PRIMARY : PRIMARY_DISABLED,
                  opacity: i === step ? 1 : 0.7,
                }}
              />
            ))}
          </div>
        </div>
      )}

      {viewStep === null && visibleQuestions.map(renderQuestion)}

      {/* Footer: the way to the full diagram, and the keyboard hint. */}
      {viewStep === null && (() => {
        const q = isStepper ? currentQuestion : (visibleQuestions.length === 1 ? visibleQuestions[0] : null);
        const action = q && !isAnswered ? diagramAction(q) : undefined;
        // A simple-choice question's map, one click away (always offered).
        const mapOnDemand = !!q && !isAnswered && !action && opensMapOnDemand(q);
        const count = keyQuestion ? rowOptions(keyQuestion).length : 0;
        const canSkip = !!q && !isAnswered && !isLocked && !hideSkip;
        if (!action && !mapOnDemand && count < 1 && !canSkip) return null;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: '0 -14px', padding: '12px 14px 0', borderTop: '1px solid #ececf1' }}>
            {((action || mapOnDemand) || canSkip) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {(action || mapOnDemand) && q && (
                  <button
                    type="button"
                    data-testid="open-epc-diagram"
                    onClick={() => (mapQuestionFor(q) || !action ? openDiagramFor(q) : applyChoice(q, action.id))}
                    disabled={isLocked || !!mapOpening[q.id]}
                    aria-busy={!!mapOpening[q.id]}
                    style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '6px 16px', background: '#ffffff', border: '1px solid #dedde5', borderRadius: 999, cursor: isLocked ? 'default' : 'pointer', fontFamily: 'inherit', textAlign: 'left' }}
                  >
                    {questionHasDiagram(q) && <QuestionDiagramThumb question={q} width={52} height={32} />}
                    {mapOpening[q.id]
                      ? <Loader2 data-testid="epc-loading" size={16} color="#4a4856" className="animate-spin" aria-hidden="true" />
                      : <Maximize2 size={16} color="#4a4856" aria-hidden="true" />}
                    <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
                      <span style={{ fontSize: '14px', fontWeight: 700, color: '#282631' }}>
                        {mapOpening[q.id]
                          ? t('chat.clarification.diagramLoading', { defaultValue: 'Preparing diagram…' })
                          : t('chat.clarification.openEpcDiagram', { defaultValue: 'Open the EPC diagram' })}
                      </span>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: '#4a4856' }}>
                        {t('chat.clarification.openEpcDiagramSub', { defaultValue: 'to select the exact part' })}
                      </span>
                    </span>
                  </button>
                )}
                {canSkip && q && (
                  <button
                    type="button"
                    data-testid="skip-question"
                    onClick={() => skipQuestion(q)}
                    title={t('chat.clarification.skipHint', { defaultValue: 'The other parts go ahead; this one stays open in the result so you can answer it later.' })}
                    style={{ marginLeft: 'auto', flexShrink: 0, padding: '10px 16px', border: '1px solid #dedde5', borderRadius: 999, background: '#ffffff', color: '#282631', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
                  >
                    {t('chat.clarification.skipShort', { defaultValue: 'Skip' })}
                  </button>
                )}
              </div>
            )}
            {count > 1 && (
              <span style={{ fontSize: '12.5px', color: '#6c6978' }}>
                {t('chat.clarification.pressToChoose', { defaultValue: 'Press 1–{{count}} to choose, or type more context', count })}
              </span>
            )}
          </div>
        );
      })()}

      {/* List/text questions submit here; a single-select row answers on
          click, so it needs no Submit. Diagram questions confirm in the map. */}
      {viewStep === null && !visibleQuestionsUseDiagram && (isStepper || !visibleQuestions.every(q =>
        q.question_type === 'single_select' && !usesMap(q)
        && !(selections[q.id] ?? []).some(id => id === OTHER_OPTION_ID || id === NONE_OPTION_ID))) && <div
        style={{
          display: 'flex',
          justifyContent: isStepper ? 'space-between' : 'flex-end',
          alignItems: 'center',
        }}
      >
        {isStepper && (
          <button
            onClick={goBack}
            disabled={step === 0}
            style={{
              padding: '7px 16px',
              fontSize: '12px',
              fontWeight: 600,
              color: step === 0 ? PRIMARY_DISABLED : PRIMARY,
              backgroundColor: 'transparent',
              border: `1px solid ${step === 0 ? PRIMARY_DISABLED : PRIMARY}`,
              borderRadius: '8px',
              cursor: step === 0 ? 'default' : 'pointer',
              transition: 'color 0.15s, border-color 0.15s',
            }}
          >
            {t('chat.clarification.back', { defaultValue: 'Back' })}
          </button>
        )}
        {isAnswered ? (
          // Read-only: no action to take — show a static confirmation instead of
          // a disabled-looking Submit button (which reads as "still pending").
          <span style={{ fontSize: '12px', fontWeight: 600, color: '#9b9ad6' }}>
            {t('chat.clarification.answerSubmitted', { defaultValue: 'Answer submitted' })}
          </span>
        ) : (
          <button
            onClick={onPrimaryClick}
            disabled={primaryDisabled}
            style={{
              padding: '7px 18px',
              fontSize: '12px',
              fontWeight: 600,
              color: '#ffffff',
              backgroundColor: primaryDisabled ? PRIMARY_DISABLED : PRIMARY,
              border: 'none',
              borderRadius: '8px',
              cursor: primaryDisabled ? 'default' : 'pointer',
              transition: 'background-color 0.15s',
            }}
          >
            {primaryLabel}
          </button>
        )}
      </div>}
      {isAnswered && waitingNext && (
        <div data-testid="waiting-next" aria-live="polite"
          style={{ position: 'absolute', inset: 0, zIndex: 5, borderRadius: 12, background: 'rgba(255,255,255,0.72)', backdropFilter: 'blur(1px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '10px 18px', borderRadius: 999, background: '#ffffff', border: '1px solid #e3e2e8', boxShadow: '0 4px 14px rgba(20,20,40,0.08)', fontSize: '14px', fontWeight: 600, color: '#4a4856' }}>
            <Loader2 size={16} className="animate-spin" aria-hidden="true" />
            {t('chat.clarification.narrowingNext', { defaultValue: 'Narrowing it down…' })}
          </span>
        </div>
      )}
    </div>
  );
};

export default StructuredQuestionForm;
